/* global fabric, File, TextEncoder, TextDecoder, escape, unescape */
/**
 * In-browser PDF editor for the custom proofsheet.
 *
 * Renders a PDF with pdf.js (already loaded globally as
 * window['pdfjs-dist/build/pdf']), lets the user annotate each page on a
 * Fabric.js overlay (window.fabric), and exports a new, vector-preserving PDF
 * with pdf-lib (window.PDFLib): untouched pages are copied verbatim, pages with
 * only overlay edits keep their original vector content with a transparent PNG
 * overlay stamped on top, and pages that were rotated or raster-edited are
 * rasterized.
 *
 * Static API:
 *   PdfEditorView.open({
 *     url,            // raw PDF url (no &action=preview)
 *     fileName,       // suggested output file name
 *     title,
 *     imageSources,   // [{key, label, loader(first,pageSize)}] for the image picker
 *     onSave          // function(File pdf)
 *   });
 */
define('pdfEditorView',[
  'module',
  'backbone',
  'marionette',
  'underscore',
  'jquery',
  'imageEditorView',
  'imageSourcePickerView',
  'template!pdfEditorView'
], function (
  Module,
  Backbone,
  Marionette,
  _,
  $,
  ImageEditorView,
  ImageSourcePickerView,
  Tpl
) {
  'use strict';

  var PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.2.228/pdf.worker.min.js';
  var SHAPE_TOOLS = ['line', 'arrow', 'rect', 'highlight'];
  // Closed shapes whose interior can take a fill color from the Style panel.
  var FILLABLE_TYPES = ['rect', 'circle', 'ellipse', 'triangle', 'polygon'];
  // Extra Fabric.js object properties to round-trip through JSON (undo / page saves).
  var CUSTOM_JSON_PROPS = ['warpOffsets', 'warpActive', 'meshActive', 'meshWarp', 'meshEditing', 'locked'];
  // Mesh warp: coarse control-grid size and how finely each cell is tessellated
  // when rendering (higher = smoother curve, more triangles per frame).
  var MESH_ROWS = 3;
  var MESH_COLS = 3;
  var MESH_SUBDIV = 6;
  var MESH_SUBDIV_DRAG = 3;
  // Live tessellation level — dropped during a drag for responsiveness, restored
  // (with a final high-quality repaint) on mouse-up.
  var meshDrawSubdiv = MESH_SUBDIV;
  // Editor state (page geometry + clean background + Fabric overlay JSON) is
  // stashed as an embedded file inside the exported PDF so re-opening the saved
  // proofsheet restores editable objects instead of a flat bitmap.
  var EDITOR_STATE_FILE = 'compuzz-proofsheet-editor.json';
  // How many attachments to pull per page when populating a sidebar image group.
  var SIDEBAR_PAGE_SIZE = 30;

  function getPdfjs() { return window['pdfjs-dist/build/pdf']; }

  // Draw a fabric.Image using two affine triangles to achieve a 4-point perspective warp.
  // ctx      — canvas 2D context, already transformed to the object's local space
  // el       — raw image element (this._element)
  // iw, ih   — image display size in natural pixels (this.width / this.height)
  // cropX/Y  — source crop offsets (this.cropX / this.cropY, default 0)
  // offsets  — {tl,tr,br,bl} each {x,y} — corner offset in natural pixels from the
  //            default corner position (±iw/2, ±ih/2)
  function renderWarpedImage(ctx, el, iw, ih, cropX, cropY, offsets) {
    var hw, hh, off, tl, tr, br, bl;
    var a1, b1, c1, d1;
    var a2, b2, c2, d2, e2, f2;

    if (!iw || !ih) { return; }
    hw = iw / 2;  hh = ih / 2;
    off = offsets || { tl: {x:0,y:0}, tr: {x:0,y:0}, br: {x:0,y:0}, bl: {x:0,y:0} };
    tl = { x: -hw + off.tl.x, y: -hh + off.tl.y };
    tr = { x:  hw + off.tr.x, y: -hh + off.tr.y };
    br = { x:  hw + off.br.x, y:  hh + off.br.y };
    bl = { x: -hw + off.bl.x, y:  hh + off.bl.y };

    // Triangle 1: source (0,0)→(iw,0)→(0,ih)  dest tl→tr→bl
    // Affine M such that M*(sx,sy) = (dx,dy):
    //   a=(tr.x-tl.x)/iw  b=(tr.y-tl.y)/iw  c=(bl.x-tl.x)/ih  d=(bl.y-tl.y)/ih  e=tl.x  f=tl.y
    a1 = (tr.x - tl.x) / iw;  b1 = (tr.y - tl.y) / iw;
    c1 = (bl.x - tl.x) / ih;  d1 = (bl.y - tl.y) / ih;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y); ctx.lineTo(tr.x, tr.y); ctx.lineTo(bl.x, bl.y);
    ctx.closePath(); ctx.clip();
    ctx.transform(a1, b1, c1, d1, tl.x, tl.y);
    ctx.drawImage(el, cropX, cropY, iw, ih, 0, 0, iw, ih);
    ctx.restore();

    // Triangle 2: source (iw,0)→(iw,ih)→(0,ih)  dest tr→br→bl
    //   a=(br.x-bl.x)/iw  b=(br.y-bl.y)/iw  c=(br.x-tr.x)/ih  d=(br.y-tr.y)/ih
    //   e=bl.x+tr.x-br.x  f=bl.y+tr.y-br.y
    a2 = (br.x - bl.x) / iw;  b2 = (br.y - bl.y) / iw;
    c2 = (br.x - tr.x) / ih;  d2 = (br.y - tr.y) / ih;
    e2 = bl.x + tr.x - br.x;  f2 = bl.y + tr.y - br.y;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(tr.x, tr.y); ctx.lineTo(br.x, br.y); ctx.lineTo(bl.x, bl.y);
    ctx.closePath(); ctx.clip();
    ctx.transform(a2, b2, c2, d2, e2, f2);
    ctx.drawImage(el, cropX, cropY, iw, ih, 0, 0, iw, ih);
    ctx.restore();
  }

  /* ----- Free-form mesh warp -------------------------------------------- */

  // Catmull-Rom interpolation of one coordinate across 4 successive knots.
  function crStep(p0, p1, p2, p3, t) {
    var t2 = t * t, t3 = t2 * t;
    return 0.5 * ((2 * p1) + (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
  }

  // Sample a 1-D list of {x,y} knots (length n+1) with a Catmull-Rom spline at
  // global parameter tt in [0,1]; endpoints are clamped (no phantom points).
  function crSample(knots, n, tt) {
    var seg = Math.min(Math.floor(tt * n), n - 1);
    var lt = tt * n - seg;
    var i0 = Math.max(0, seg - 1), i1 = seg, i2 = Math.min(n, seg + 1), i3 = Math.min(n, seg + 2);
    return {
      x: crStep(knots[i0].x, knots[i1].x, knots[i2].x, knots[i3].x, lt),
      y: crStep(knots[i0].y, knots[i1].y, knots[i2].y, knots[i3].y, lt)
    };
  }

  // Upsample a coarse (R+1)x(C+1) grid of {x,y} into a smooth (RS+1)x(CS+1) grid.
  function meshUpsample(coarse, R, C, RS, CS) {
    var rowsFine = [], r, i, j;
    for (r = 0; r <= R; r++) {
      rowsFine[r] = [];
      for (j = 0; j <= CS; j++) { rowsFine[r][j] = crSample(coarse[r], C, j / CS); }
    }
    var fine = [];
    for (i = 0; i <= RS; i++) {
      fine[i] = [];
      for (j = 0; j <= CS; j++) {
        var col = [];
        for (r = 0; r <= R; r++) { col.push(rowsFine[r][j]); }
        fine[i][j] = crSample(col, R, i / RS);
      }
    }
    return fine;
  }

  // Draw one textured quad (source rect sx,sy,sw,sh) onto the 4 destination
  // points A,B,C,D (clockwise from top-left) as two affine-mapped triangles.
  // Destination points are nudged out from the quad centre by ~0.5px so adjacent
  // cells overlap slightly and don't leave hairline seams.
  function drawMeshQuad(ctx, el, sx, sy, sw, sh, A, B, C, D) {
    var cx = (A.x + B.x + C.x + D.x) / 4;
    var cy = (A.y + B.y + C.y + D.y) / 4;
    var grow = function (p) {
      var dx = p.x - cx, dy = p.y - cy, len = Math.sqrt(dx * dx + dy * dy) || 1;
      return { x: p.x + dx / len * 0.6, y: p.y + dy / len * 0.6 };
    };
    var a = grow(A), b = grow(B), c = grow(C), d = grow(D);

    // Triangle 1: src (0,0)->(sw,0)->(0,sh)  dest a->b->d
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.lineTo(d.x, d.y);
    ctx.closePath(); ctx.clip();
    ctx.transform((b.x - a.x) / sw, (b.y - a.y) / sw,
      (d.x - a.x) / sh, (d.y - a.y) / sh, a.x, a.y);
    ctx.drawImage(el, sx, sy, sw, sh, 0, 0, sw, sh);
    ctx.restore();

    // Triangle 2: src (sw,0)->(sw,sh)->(0,sh)  dest b->c->d
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(b.x, b.y); ctx.lineTo(c.x, c.y); ctx.lineTo(d.x, d.y);
    ctx.closePath(); ctx.clip();
    ctx.transform((c.x - d.x) / sw, (c.y - d.y) / sw,
      (c.x - b.x) / sh, (c.y - b.y) / sh,
      d.x + b.x - c.x, d.y + b.y - c.y);
    ctx.drawImage(el, sx, sy, sw, sh, 0, 0, sw, sh);
    ctx.restore();
  }

  // Render a fabric.Image through its mesh-warp control grid. `mesh` is
  // { rows, cols, points: [[{x,y}...]...] } — points are offsets (natural px)
  // from each control point's regular lattice position.
  function renderMeshImage(ctx, el, iw, ih, cropX, cropY, mesh) {
    if (!iw || !ih || !mesh || !mesh.points) { return; }
    var R = mesh.rows, C = mesh.cols, hw = iw / 2, hh = ih / 2;
    var coarse = [], r, c;
    for (r = 0; r <= R; r++) {
      coarse[r] = [];
      for (c = 0; c <= C; c++) {
        var off = (mesh.points[r] && mesh.points[r][c]) || { x: 0, y: 0 };
        coarse[r][c] = { x: -hw + (c / C) * iw + off.x, y: -hh + (r / R) * ih + off.y };
      }
    }
    var sub = meshDrawSubdiv || MESH_SUBDIV;
    var RS = R * sub, CS = C * sub;
    var fine = meshUpsample(coarse, R, C, RS, CS);
    var sw = iw / CS, sh = ih / RS, i, j;
    for (i = 0; i < RS; i++) {
      for (j = 0; j < CS; j++) {
        drawMeshQuad(ctx, el, cropX + j * sw, cropY + i * sh, sw, sh,
          fine[i][j], fine[i][j + 1], fine[i + 1][j + 1], fine[i + 1][j]);
      }
    }
  }

  // Identity (undeformed) control grid.
  function identityMesh(rows, cols) {
    var pts = [], r, c;
    for (r = 0; r <= rows; r++) {
      pts[r] = [];
      for (c = 0; c <= cols; c++) { pts[r][c] = { x: 0, y: 0 }; }
    }
    return { rows: rows, cols: cols, points: pts, preset: '', amount: 0 };
  }

  // Fill a control grid from a named curve preset. `amount` in [-1, 1].
  //   arc   — both edges bow along a parabola (banana / text-on-a-curve)
  //   arch  — only the top edge bows, the base stays put
  //   bulge — the centre swells outward (dome / ball / fisheye)
  function presetMesh(rows, cols, w, h, type, amount) {
    var mesh = identityMesh(rows, cols);
    mesh.preset = type;
    mesh.amount = amount;
    for (var r = 0; r <= rows; r++) {
      for (var c = 0; c <= cols; c++) {
        var u = c / cols, v = r / rows;         // 0..1 across / down
        var ku = 1 - Math.pow(2 * u - 1, 2);    // 1 at centre-column, 0 at sides
        var dx = 0, dy = 0;
        if (type === 'arc') {
          dy = amount * 0.5 * h * ku;
        } else if (type === 'arch') {
          dy = amount * 0.55 * h * ku * (1 - v);
        } else if (type === 'bulge') {
          var kv = 1 - Math.pow(2 * v - 1, 2);
          var f = Math.max(0, ku * kv);
          dx = amount * 0.32 * w * (2 * u - 1) * f;
          dy = amount * 0.32 * h * (2 * v - 1) * f;
        }
        mesh.points[r][c] = { x: dx, y: dy };
      }
    }
    return mesh;
  }

  var View = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'pdf-editor-view',

    ui: {
      canvas: '.pe-canvas',
      canvasArea: '.pe-canvas-area',
      canvasHost: '.pe-canvas-host',
      loader: '.pe-loader',
      error: '.pe-error',
      zoomLabel: '.pe-zoom-label',
      tools: '.pe-tool[data-tool]',
      strokeColor: '.pe-stroke-color',
      strokeWidth: '.pe-stroke-width',
      fillOn: '.pe-fill-on',
      fillColor: '.pe-fill-color',
      curveType: '.pe-curve-type',
      curveAmount: '.pe-curve-amount',
      fontSize: '.pe-font-size',
      pageW: '.pe-page-w',
      pageH: '.pe-page-h',
      pageUnit: '.pe-page-unit',
      refsToggleGroup: '.pe-refs-toggle-group',
      refsToggle: '.pe-refs-toggle',
      refs: '.pe-refs',
      refsList: '.pe-refs-list',
      refLightbox: '.pe-ref-lightbox',
      refLightboxImg: '.pe-ref-lightbox-img',
      refLightboxCap: '.pe-ref-lightbox-cap',
      sidebar: '.pe-sidebar',
      imgColA: '.pe-img-col-a',
      imgColB: '.pe-img-col-b',
      uploadInput: '.pe-upload-input',
      modal: '.pdf-editor-modal',
      ctxMenu: '.pe-ctx-menu',
      ctxTransform: '.pe-ctx-transform',
      ctxW: '.pe-ctx-w',
      ctxH: '.pe-ctx-h',
      ctxUnit: '.pe-ctx-unit',
      ctxLock: '.pe-ctx-lock',
      ctxOpacity: '.pe-ctx-opacity',
      ctxAlpha: '.pe-ctx-alpha',
      ctxAlphaVal: '.pe-ctx-alpha-val',
      ctxAlphaBadge: '.pe-ctx-alpha-badge',
      ctxLockLabel: '.pe-ctx-lock-label'
    },

    events: {
      'click .pe-tool[data-tool]': 'onToolClick',
      'click [data-action="insert-image"]': 'onInsertImage',
      'click [data-action="free-transform"]': 'toggleFreeTransform',
      'click [data-action="warp"]': 'toggleWarp',
      'click [data-action="mesh-warp"]': 'toggleMeshWarp',
      'click [data-action="rotate"]': 'onRotateActive',
      'click [data-action="extract"]': 'onExtractActive',
      'click [data-action="raster-edit"]': 'onRasterActive',
      'click [data-action="delete-object"]': 'deleteSelected',
      'click [data-action="undo"]': 'undo',
      'click [data-action="redo"]': 'redo',
      'click [data-stamp]': 'onStampClick',
      'click [data-action="zoom-in"]': 'zoomIn',
      'click [data-action="zoom-out"]': 'zoomOut',
      'click [data-action="zoom-reset"]': 'zoomReset',
      'click [data-action="fit-height"]': 'fitPageHeightToContent',
      'click [data-action="cancel"]': 'cancel',
      'click [data-action="save"]': 'save',
      'input @ui.strokeColor': 'onStrokeColorChange',
      'input @ui.strokeWidth': 'onStrokeWidthChange',
      'change @ui.fillOn': 'onFillChange',
      'input @ui.fillColor': 'onFillChange',
      'change @ui.curveType': 'onCurveCommit',
      'input @ui.curveAmount': 'onCurveInput',
      'change @ui.curveAmount': 'onCurveCommit',
      'input @ui.fontSize': 'onFontSizeChange',
      'change @ui.pageW': 'onPageSizeChange',
      'change @ui.pageH': 'onPageSizeChange',
      'change @ui.pageUnit': 'onPageUnitChange',
      'click [data-action="toggle-sidebar"]': 'toggleSidebar',
      'click .pe-ref': 'onRefClick',
      'click @ui.refLightbox': 'closeRefLightbox',
      'click [data-action="upload-image"]': 'onSidebarUploadClick',
      'change @ui.uploadInput': 'onSidebarUploadChange',
      'click .pe-src-item': 'onSidebarImageClick',
      'click .pe-src-more': 'onSidebarLoadMore',
      'click .pe-ctx-item[data-ctx]': 'onContextAction',
      'change @ui.ctxW': 'onCtxDimChange',
      'change @ui.ctxH': 'onCtxDimChange',
      'keydown @ui.ctxW': 'onCtxDimKey',
      'keydown @ui.ctxH': 'onCtxDimKey',
      'click @ui.ctxLock': 'onCtxLockToggle',
      'input @ui.ctxAlpha': 'onCtxAlphaInput',
      'change @ui.ctxAlpha': 'onCtxAlphaCommit'
    },

    initialize: function (options) {
      this.options = options || {};
      this.pages = [];
      this.activeIndex = -1;
      this.activeTool = 'select';
      this.userZoom = 1;
      this.fitScale = 1;
      this.strokeColor = '#e11d48';
      this.strokeWidth = 3;
      this.fillColor = '#ffd54a';
      this.fillEnabled = false;
      this.fontSize = 24;
      this.pageUnit = 'mm';
      this.undoStack = [];
      this.redoStack = [];
      this._objectUrls = [];
      this._drawing = null;
      this.freeTransform = false;
      this.ctxLockAspect = true;
    },

    onShow: function () {
      this.renderReferences();
      this.renderImageSources();

      // Paste an image straight onto the page (screenshot, copied picture…).
      this._pasteHandler = _.bind(this.onPaste, this);
      document.addEventListener('paste', this._pasteHandler);

      // Delete / Backspace removes the selected object(s).
      this._keydownHandler = _.bind(this.onKeyDown, this);
      document.addEventListener('keydown', this._keydownHandler);

      // Right-click context menu on a selected object.
      this._ctxHandler = _.bind(this.onContextMenu, this);
      if (this.ui.canvasHost[0]) {
        this.ui.canvasHost[0].addEventListener('contextmenu', this._ctxHandler);
      }
      // pdf.js is only needed when opening an existing PDF; blank/create mode
      // just needs Fabric (editing) and pdf-lib (export).
      var needsPdfjs = !this.options.blank;
      if (!window.fabric || !window.PDFLib || (needsPdfjs && !getPdfjs())) {
        this.showError(_.i18n('pdfEditor.libsMissing', null,
          'PDF editor libraries failed to load. Please check your connection and retry.'));
        return;
      }
      if (this.options.blank) {
        this.createBlankDoc();
      } else {
        this.showLoader(true);
        this.loadPdf();
      }
    },

    onDestroy: function () {
      if (this._pasteHandler) {
        document.removeEventListener('paste', this._pasteHandler);
        this._pasteHandler = null;
      }
      if (this._keydownHandler) {
        document.removeEventListener('keydown', this._keydownHandler);
        this._keydownHandler = null;
      }
      if (this._ctxHandler && this.ui.canvasHost && this.ui.canvasHost[0]) {
        this.ui.canvasHost[0].removeEventListener('contextmenu', this._ctxHandler);
        this._ctxHandler = null;
      }
      this.hideContextMenu();
      if (this.fabricCanvas) {
        this.fabricCanvas.dispose();
        this.fabricCanvas = null;
      }
      _.each(this._objectUrls, function (u) {
        try { window.URL.revokeObjectURL(u); } catch (e) { /* ignore */ }
      });
    },

    // Grab the first image on the clipboard and drop it on the page. Text /
    // non-image pastes are left alone (so pasting into a text box still works).
    onPaste: function (e) {
      if (!this.fabricCanvas) { return; }
      // The raster image editor opens as a modal on top of this one and has its
      // own paste handler — let it win while it's up.
      if (document.querySelector('.image-editor-modal')) { return; }
      var active = this.fabricCanvas.getActiveObject();
      if (active && active.isEditing) { return; } // typing in a text box
      var dt = e.clipboardData || window.clipboardData;
      if (!dt || !dt.items) { return; }
      for (var i = 0; i < dt.items.length; i++) {
        var item = dt.items[i];
        if (item.kind === 'file' && item.type.indexOf('image/') === 0) {
          var file = item.getAsFile();
          if (!file) { continue; }
          e.preventDefault();
          var url = window.URL.createObjectURL(file);
          this._objectUrls.push(url);
          this.insertImage(url);
          return;
        }
      }
    },

    // Keyboard: Delete / Backspace removes the current selection, unless a text
    // box is being edited or focus is in a form field / the raster image editor.
    onKeyDown: function (e) {
      if (e.key === 'Escape') { this.hideContextMenu(); return; }
      if (document.querySelector('.image-editor-modal') || !this.fabricCanvas) { return; }
      var tag = (e.target && e.target.tagName) || '';
      var inField = (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (e.target && e.target.isContentEditable));

      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z' || e.key === 'y' || e.key === 'Y')) {
        if (inField) { return; }
        e.preventDefault();
        if (e.key === 'y' || e.key === 'Y' || e.shiftKey) { this.redo(); } else { this.undo(); }
        return;
      }

      if (e.key !== 'Delete' && e.key !== 'Backspace') { return; }
      if (inField) { return; }
      var active = this.fabricCanvas.getActiveObject();
      if (!active || active.isEditing) { return; }
      e.preventDefault();
      this.deleteSelected();
    },

    /* --------------------------------------------------------------- */
    /* Loading                                                          */
    /* --------------------------------------------------------------- */

    loadPdf: function () {
      var that = this;
      var url = this.options.url;

      this.fetchBytes(url).then(function (bytes) {
        that.originalBytes = bytes;
        var pdfjsLib = getPdfjs();
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        // pdf.js detaches the buffer it's given — hand it a copy, keep the
        // original for pdf-lib on export.
        var loadingTask = pdfjsLib.getDocument({ data: bytes.slice(0) });
        loadingTask.promise.then(function (pdf) {
          that.renderAllPages(pdf);
        }, function (reason) {
          that.showError(_.i18n('pdfEditor.loadError', null, 'Could not open the PDF.'));
          if (window.console) { console.error(reason); }
        });
      }, function () {
        that.showError(_.i18n('pdfEditor.loadError', null, 'Could not open the PDF.'));
      });
    },

    fetchBytes: function (url) {
      var defer = $.Deferred();
      if (window.fetch) {
        window.fetch(url, { credentials: 'include' }).then(function (resp) {
          return resp.arrayBuffer();
        }).then(function (buf) {
          defer.resolve(buf);
        })['catch'](function () { defer.reject(); });
      } else {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'arraybuffer';
        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) { defer.resolve(xhr.response); }
          else { defer.reject(); }
        };
        xhr.onerror = function () { defer.reject(); };
        xhr.send();
      }
      return defer.promise();
    },

    renderAllPages: function (pdf) {
      var that = this;
      // Single-page editor: only the first page is loaded and exported.
      var n = 1;
      var chain = $.Deferred().resolve().promise();

      var addPage = function (pageNum) {
        return function () {
          var d = $.Deferred();
          pdf.getPage(pageNum).then(function (page) {
            var vp1 = page.getViewport({ scale: 1 });
            var scale = Math.min(2.5, Math.max(1, 1400 / Math.max(vp1.width, vp1.height)));
            var vp = page.getViewport({ scale: scale });
            var canvas = document.createElement('canvas');
            canvas.width = Math.ceil(vp.width);
            canvas.height = Math.ceil(vp.height);
            var ctx = canvas.getContext('2d');
            page.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
              that.pages.push({
                srcIndex: pageNum - 1,
                pdfPage: page,
                scale: scale,
                ptW: vp1.width,
                ptH: vp1.height,
                baseW: canvas.width,
                baseH: canvas.height,
                baseDataUrl: canvas.toDataURL('image/png'),
                srcHasRotate: (page.rotate % 360) !== 0,
                overlayJSON: null,
                rotation: 0,
                rasterEdited: false,
                decomposed: false,
                deleted: false
              });
              d.resolve();
            }, function () { d.resolve(); });
          }, function () { d.resolve(); });
          return d.promise();
        };
      };

      for (var i = 1; i <= n; i++) {
        chain = chain.then(addPage(i));
      }

      chain.then(function () {
        that.restoreEditorStateFromPdf(pdf).then(function () {
          that.showLoader(false);
          if (that.pages.length > 0) {
            that.activatePage(0);
          } else {
            that.showError(_.i18n('pdfEditor.loadError', null, 'Could not open the PDF.'));
          }
        });
      });
    },

    /* --------------------------------------------------------------- */
    /* Editor-state round-trip (re-editable objects after save)         */
    /* --------------------------------------------------------------- */

    // On save the editor stashes each page's geometry + clean background bitmap
    // + Fabric overlay JSON as an embedded file inside the exported PDF. When
    // that PDF is re-opened, this reads it back so the previously created objects
    // are editable again instead of being baked into a single flat image.
    restoreEditorStateFromPdf: function (pdf) {
      var that = this;
      return new Promise(function (resolve) {
        if (!pdf || typeof pdf.getAttachments !== 'function') { resolve(); return; }
        var done = function () { resolve(); };
        try {
          pdf.getAttachments().then(function (atts) {
            try {
              var entry = null;
              if (atts) {
                entry = atts[EDITOR_STATE_FILE] || _.find(_.values(atts), function (a) {
                  return a && a.filename === EDITOR_STATE_FILE;
                });
              }
              var content = entry && (entry.content || entry);
              if (content && content.length) {
                var json = JSON.parse(that.utf8Decode(content));
                that.applyEditorState(json);
              }
            } catch (e) {
              if (window.console) { console.warn('Proofsheet editor state ignored:', e); }
            }
            done();
          }, done);
        } catch (e2) { done(); }
      });
    },

    applyEditorState: function (json) {
      if (!json || !_.isArray(json.pages) || !json.pages.length) { return; }
      var st = json.pages[0];
      var pg = this.pages[0];
      if (!pg || !st) { return; }
      if (st.bg) { pg.baseDataUrl = st.bg; }
      pg.overlayJSON = (st.overlay && st.overlay.objects) ? { objects: st.overlay.objects } : null;
      pg.rotation = st.rotation || 0;
      if (st.ptW) { pg.ptW = st.ptW; }
      if (st.ptH) { pg.ptH = st.ptH; }
      if (st.baseW) { pg.baseW = st.baseW; }
      if (st.baseH) { pg.baseH = st.baseH; }
      if (st.scale) { pg.scale = st.scale; }
      pg.decomposed = !!st.decomposed;
      pg.resized = !!st.resized;
      // The PDF we just opened already has the overlay flattened onto its vector
      // content, so this page must re-export through the raster path (clean bg +
      // live overlay) rather than the copy-vector-and-stamp path — otherwise the
      // annotations would be doubled.
      pg.rasterEdited = true;
      pg.restoredFromState = true;
    },

    // Serialize the current editor state and embed it in the export document.
    attachEditorState: function (outDoc) {
      try {
        if (!outDoc || typeof outDoc.attach !== 'function') { return Promise.resolve(); }
        var pages = _.filter(this.pages, function (p) { return !p.deleted; });
        var state = {
          v: 1,
          pages: _.map(pages, function (pg) {
            return {
              ptW: pg.ptW,
              ptH: pg.ptH,
              baseW: pg.baseW,
              baseH: pg.baseH,
              scale: pg.scale,
              rotation: pg.rotation || 0,
              decomposed: !!pg.decomposed,
              resized: !!pg.resized,
              bg: pg.baseDataUrl || null,
              overlay: (pg.overlayJSON && pg.overlayJSON.objects && pg.overlayJSON.objects.length) ?
                { objects: pg.overlayJSON.objects } : null
            };
          })
        };
        var bytes = this.utf8Encode(JSON.stringify(state));
        return Promise.resolve(outDoc.attach(bytes, EDITOR_STATE_FILE, {
          mimeType: 'application/json',
          description: 'Compuzz proofsheet editor state',
          creationDate: new Date(),
          modificationDate: new Date()
        }));
      } catch (e) {
        if (window.console) { console.warn('Could not embed proofsheet editor state:', e); }
        return Promise.resolve();
      }
    },

    utf8Encode: function (str) {
      if (window.TextEncoder) { return new TextEncoder().encode(str); }
      var bin = unescape(encodeURIComponent(str));
      var arr = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) { arr[i] = bin.charCodeAt(i); }
      return arr;
    },

    utf8Decode: function (bytes) {
      if (window.TextDecoder) { return new TextDecoder('utf-8').decode(bytes); }
      var bin = '';
      for (var i = 0; i < bytes.length; i++) { bin += String.fromCharCode(bytes[i]); }
      return decodeURIComponent(escape(bin));
    },

    /* --------------------------------------------------------------- */
    /* Blank / create mode                                              */
    /* --------------------------------------------------------------- */

    // Build a fresh blank page (white background) at A4 by default, or matching
    // the current page's size / the size requested via options.
    makeBlankPage: function () {
      var ptW = this.options.pageWidthPt || 595.28;
      var ptH = this.options.pageHeightPt || 841.89;
      if (this.activeIndex !== -1 && this.pages[this.activeIndex]) {
        ptW = this.pages[this.activeIndex].ptW;
        ptH = this.pages[this.activeIndex].ptH;
      }
      var scale = Math.min(2.5, Math.max(1, 1400 / Math.max(ptW, ptH)));
      var baseW = Math.round(ptW * scale);
      var baseH = Math.round(ptH * scale);
      var canvas = document.createElement('canvas');
      canvas.width = baseW;
      canvas.height = baseH;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, baseW, baseH);
      return {
        srcIndex: -1,
        blank: true,
        pdfPage: null,
        scale: scale,
        ptW: ptW,
        ptH: ptH,
        baseW: baseW,
        baseH: baseH,
        baseDataUrl: canvas.toDataURL('image/png'),
        srcHasRotate: false,
        overlayJSON: null,
        rotation: 0,
        rasterEdited: false,
        decomposed: false,
        deleted: false
      };
    },

    createBlankDoc: function () {
      this.showLoader(false);
      this.pages.push(this.makeBlankPage());
      this.activatePage(0);
    },

    /* --------------------------------------------------------------- */
    /* Thumbnails / pages                                               */
    /* --------------------------------------------------------------- */

    onExtractActive: function () {
      if (this.activeIndex !== -1) { this.decomposePage(this.activeIndex); }
    },

    onRotateActive: function () {
      if (this.activeIndex !== -1) { this.rotatePage(this.activeIndex); }
    },

    // "Edit image": if an image object is selected, edit that object's picture;
    // otherwise fall back to editing the whole page bitmap.
    onRasterActive: function () {
      var sel = this.fabricCanvas && this.fabricCanvas.getActiveObject();
      if (sel && sel.type === 'image') {
        this.editSelectedImage(sel);
      } else if (this.activeIndex !== -1) {
        this.rasterEditPage(this.activeIndex);
      }
    },

    editSelectedImage: function (img) {
      var that = this;
      var src = (typeof img.getSrc === 'function' && img.getSrc()) ||
        (img._element && (img._element.src || img._element.currentSrc)) || img.src;
      if (!src) {
        this.notify(_.i18n('pdfEditor.editImageNoSrc', null, 'This image has no editable source.'));
        return;
      }
      ImageEditorView.open({
        url: src,
        fileName: 'image.png',
        title: _.i18n('pdfEditor.rasterEdit', null, 'Edit image'),
        onSave: function (file) {
          var reader = new FileReader();
          reader.onload = function () {
            that.recordUndo();
            var center = img.getCenterPoint();
            img.setSrc(reader.result, function () {
              // Keep the picture visually anchored where it was.
              img.setPositionByOrigin(center, 'center', 'center');
              img.setCoords();
              img.dirty = true;
              that.fabricCanvas.requestRenderAll();
            }, { crossOrigin: 'anonymous' });
          };
          reader.readAsDataURL(file);
        }
      });
    },

    // Rotate the page 90° clockwise. The background bitmap and any annotations
    // rotate together so what you see is what gets exported. Annotations are
    // transformed on an offscreen canvas (no race with the live canvas), so this
    // works whether or not the page is currently active.
    rotatePage: function (index) {
      var that = this;
      var pg = this.pages[index];
      if (this.activeIndex === index) { this.saveCurrentPageOverlay(); }

      var swap = (pg.rotation === 90 || pg.rotation === 270);
      var oldW = swap ? pg.baseH : pg.baseW;
      var oldH = swap ? pg.baseW : pg.baseH;

      var finish = function () {
        pg.rotation = (pg.rotation + 90) % 360;
        that.activatePage(index, true);
      };

      var objects = (pg.overlayJSON && pg.overlayJSON.objects) ? pg.overlayJSON.objects : [];
      if (!objects.length) { finish(); return; }

      var staticCanvas = new fabric.StaticCanvas(null, { width: oldW, height: oldH });
      staticCanvas.loadFromJSON({ objects: objects }, function () {
        _.each(staticCanvas.getObjects(), function (o) {
          var c = o.getCenterPoint();
          var nc = new fabric.Point(oldH - c.y, c.x);
          o.angle = (o.angle + 90) % 360;
          o.setPositionByOrigin(nc, 'center', 'center');
          o.setCoords();
        });
        var json = staticCanvas.toJSON(CUSTOM_JSON_PROPS);
        pg.overlayJSON = { objects: json.objects };
        staticCanvas.dispose();
        finish();
      });
    },

    /* --------------------------------------------------------------- */
    /* Fabric canvas                                                    */
    /* --------------------------------------------------------------- */

    activatePage: function (index, keepUndo) {
      if (this.activeIndex !== -1 && this.activeIndex !== index) {
        this.saveCurrentPageOverlay();
      }
      this.activeIndex = index;
      if (!keepUndo) { this.undoStack = []; this.redoStack = []; }
      this.updateHistoryButtons();

      var pg = this.pages[index];
      var that = this;

      if (!this.fabricCanvas) {
        this.fabricCanvas = new fabric.Canvas(this.ui.canvas[0], {
          preserveObjectStacking: true,
          selection: true
        });
        this.bindCanvasEvents();
      }
      var canvas = this.fabricCanvas;
      canvas.clear();
      // Display the page at its (possibly rotated) on-screen orientation.
      var swap = (pg.rotation === 90 || pg.rotation === 270);
      var w = swap ? pg.baseH : pg.baseW;
      var h = swap ? pg.baseW : pg.baseH;
      canvas.setWidth(w);
      canvas.setHeight(h);
      // Remember the page's unscaled on-screen size. applyZoom multiplies these
      // by the zoom factor and resizes the canvas element; reading the size back
      // off the (already-scaled) canvas on the next zoom would compound the
      // scaling, so the base dimensions must come from here, not getWidth().
      this._canvasW = w;
      this._canvasH = h;

      var applyBg = function () {
        that.displayBg(pg).then(function (bgUrl) {
          fabric.Image.fromURL(bgUrl, function (img) {
            img.set({
              left: 0, top: 0, originX: 'left', originY: 'top',
              scaleX: w / img.width, scaleY: h / img.height
            });
            canvas.setBackgroundImage(img, function () {
              canvas.requestRenderAll();
              that.fitAndZoom();
            });
          }, { crossOrigin: 'anonymous' });
        });
      };

      if (pg.overlayJSON && pg.overlayJSON.objects && pg.overlayJSON.objects.length) {
        canvas.loadFromJSON({ objects: pg.overlayJSON.objects }, function () {
          that.restoreWarpedImages(canvas);
          applyBg();
        });
      } else {
        applyBg();
      }

      this.setTool(this.activeTool, true);
      this.syncPageSizeInputs();
    },

    saveCurrentPageOverlay: function () {
      if (!this.fabricCanvas || this.activeIndex === -1) { return; }
      var pg = this.pages[this.activeIndex];
      if (!pg || pg.deleted) { return; }
      var json = this.fabricCanvas.toJSON(CUSTOM_JSON_PROPS);
      delete json.backgroundImage;
      delete json.background;
      pg.overlayJSON = json;
      // Refresh the thumbnail with the current overlay baked in is skipped for
      // perf — thumbnails show the page background only.
    },

    bindCanvasEvents: function () {
      var canvas = this.fabricCanvas;
      canvas.on('mouse:down', _.bind(this.onCanvasMouseDown, this));
      canvas.on('mouse:move', _.bind(this.onCanvasMouseMove, this));
      canvas.on('mouse:up', _.bind(this.onCanvasMouseUp, this));
      // Mesh-warp level-of-detail: coarse tessellation during any drag, full
      // quality once it ends.
      canvas.on('mouse:down', function () { meshDrawSubdiv = MESH_SUBDIV_DRAG; });
      canvas.on('mouse:up', _.bind(function () {
        meshDrawSubdiv = MESH_SUBDIV;
        if (this.fabricCanvas) {
          _.each(this.fabricCanvas.getObjects(), function (o) {
            if (o.meshActive) { o.dirty = true; }
          });
          this.fabricCanvas.requestRenderAll();
        }
      }, this));
      canvas.on('selection:created', _.bind(this.onSelection, this));
      canvas.on('selection:updated', _.bind(this.onSelection, this));
      canvas.on('selection:cleared', _.bind(this.refreshWarpToolbar, this));
      canvas.on('object:modified', _.bind(function () {
        if (this._preEditJSON) {
          this.recordUndo(this._preEditJSON);
          this._preEditJSON = null;
        }
      }, this));
    },

    fitAndZoom: function () {
      if (!this.fabricCanvas) { return; }
      var area = this.ui.canvasArea[0];
      var pad = 40;
      var maxW = (area ? area.clientWidth : 700) - pad;
      var maxH = (area ? area.clientHeight : 500) - pad;
      var w = this._canvasW || this.fabricCanvas.getWidth();
      var h = this._canvasH || this.fabricCanvas.getHeight();
      this.fitScale = Math.min(maxW / w, maxH / h, 1);
      if (!isFinite(this.fitScale) || this.fitScale <= 0) { this.fitScale = 1; }
      this.applyZoom();
    },

    applyZoom: function () {
      var z = this.fitScale * this.userZoom;
      var w = this._canvasW || this.fabricCanvas.getWidth();
      var h = this._canvasH || this.fabricCanvas.getHeight();
      this.fabricCanvas.setZoom(z);
      this.fabricCanvas.setDimensions({ width: w * z, height: h * z });
      if (this.ui.zoomLabel) { this.ui.zoomLabel.text(Math.round(z * 100) + '%'); }
    },

    zoomIn: function () { this.userZoom = Math.min(this.userZoom * 1.25, 6); this.applyZoom(); },
    zoomOut: function () { this.userZoom = Math.max(this.userZoom / 1.25, 0.2); this.applyZoom(); },
    zoomReset: function () { this.userZoom = 1; this.applyZoom(); },

    /* --------------------------------------------------------------- */
    /* Tools                                                            */
    /* --------------------------------------------------------------- */

    onToolClick: function (e) {
      this.setTool($(e.currentTarget).attr('data-tool'));
    },

    setTool: function (tool, silent) {
      this.activeTool = tool;
      this.ui.tools.removeClass('active');
      this.ui.tools.filter('[data-tool="' + tool + '"]').addClass('active');

      if (!this.fabricCanvas) { return; }
      var canvas = this.fabricCanvas;

      canvas.isDrawingMode = (tool === 'pen');
      if (tool === 'pen') {
        canvas.freeDrawingBrush.color = this.strokeColor;
        canvas.freeDrawingBrush.width = this.strokeWidth;
      }
      // Lock selection while drawing shapes so the drag creates a shape.
      canvas.selection = (tool === 'select');
      canvas.skipTargetFind = (_.contains(SHAPE_TOOLS, tool));
      canvas.defaultCursor = (_.contains(SHAPE_TOOLS, tool) || tool === 'text') ? 'crosshair' : 'default';

      if (tool === 'text' && !silent) {
        // Text is placed on the next canvas click (handled in mouse:down).
        this._pendingText = true;
      } else {
        this._pendingText = (tool === 'text');
      }
    },

    onCanvasMouseDown: function (opt) {
      var canvas = this.fabricCanvas;

      if (this.activeTool === 'select') {
        if (opt.target) { this._preEditJSON = this.serializeObjects(); }
        return;
      }

      var p = canvas.getPointer(opt.e);

      if (this.activeTool === 'text') {
        this.addText(p.x, p.y);
        this.setTool('select');
        return;
      }

      if (_.contains(SHAPE_TOOLS, this.activeTool)) {
        this.recordUndo();
        this._drawing = { startX: p.x, startY: p.y, obj: null };
      }
    },

    onCanvasMouseMove: function (opt) {
      if (!this._drawing) { return; }
      var canvas = this.fabricCanvas;
      var p = canvas.getPointer(opt.e);
      var d = this._drawing;

      if (d.obj) { canvas.remove(d.obj); }
      d.obj = this.makeShape(this.activeTool, d.startX, d.startY, p.x, p.y);
      if (d.obj) {
        canvas.add(d.obj);
        canvas.requestRenderAll();
      }
    },

    onCanvasMouseUp: function () {
      if (!this._drawing) { return; }
      var d = this._drawing;
      this._drawing = null;
      if (d.obj) {
        this.fabricCanvas.setActiveObject(d.obj);
        this.fabricCanvas.requestRenderAll();
      } else {
        // Nothing drawn — drop the snapshot we pushed on mouse:down.
        this.undoStack.pop();
        this.updateHistoryButtons();
      }
      this.setTool('select');
    },

    makeShape: function (tool, x1, y1, x2, y2) {
      var color = this.strokeColor;
      var width = this.strokeWidth;

      if (tool === 'rect' || tool === 'highlight') {
        var left = Math.min(x1, x2);
        var top = Math.min(y1, y2);
        var w = Math.abs(x2 - x1);
        var h = Math.abs(y2 - y1);
        if (w < 2 && h < 2) { return null; }
        if (tool === 'highlight') {
          return new fabric.Rect({
            left: left, top: top, width: w, height: h,
            fill: color, opacity: 0.35, stroke: null,
            globalCompositeOperation: 'multiply'
          });
        }
        return new fabric.Rect({
          left: left, top: top, width: w, height: h,
          fill: this.fillEnabled ? this.fillColor : 'transparent',
          stroke: color, strokeWidth: width
        });
      }

      if (tool === 'line') {
        if (Math.abs(x2 - x1) < 2 && Math.abs(y2 - y1) < 2) { return null; }
        return new fabric.Line([x1, y1, x2, y2], {
          stroke: color, strokeWidth: width, strokeLineCap: 'round'
        });
      }

      if (tool === 'arrow') {
        if (Math.abs(x2 - x1) < 2 && Math.abs(y2 - y1) < 2) { return null; }
        return this.makeArrow(x1, y1, x2, y2, color, width);
      }
      return null;
    },

    makeArrow: function (x1, y1, x2, y2, color, width) {
      var line = new fabric.Line([x1, y1, x2, y2], {
        stroke: color, strokeWidth: width, strokeLineCap: 'round'
      });
      var angle = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
      var headSize = Math.max(10, width * 3.5);
      var head = new fabric.Triangle({
        left: x2, top: y2,
        originX: 'center', originY: 'center',
        width: headSize, height: headSize,
        fill: color,
        angle: angle + 90
      });
      return new fabric.Group([line, head]);
    },

    addText: function (x, y) {
      this.recordUndo();
      var t = new fabric.Textbox(_.i18n('pdfEditor.textPlaceholder', null, 'Text'), {
        left: x, top: y,
        fontSize: this.fontSize,
        fill: this.strokeColor,
        fontFamily: 'Arial',
        width: Math.max(120, this.fontSize * 6),
        editable: true
      });
      this.fabricCanvas.add(t);
      this.fabricCanvas.setActiveObject(t);
      t.enterEditing();
      t.selectAll();
      this.fabricCanvas.requestRenderAll();
    },

    onStampClick: function (e) {
      var stamp = $(e.currentTarget).attr('data-stamp');
      this.recordUndo();
      var canvas = this.fabricCanvas;
      var cx = canvas.getWidth() / 2;
      var cy = canvas.getHeight() / 2;
      var obj;

      if (stamp === 'date') {
        var d = new Date();
        var txt = d.getFullYear() + '-' +
          ('0' + (d.getMonth() + 1)).slice(-2) + '-' +
          ('0' + d.getDate()).slice(-2);
        obj = new fabric.Textbox(txt, {
          left: cx, top: cy, originX: 'center', originY: 'center',
          fontSize: this.fontSize, fill: this.strokeColor, fontFamily: 'Arial'
        });
      } else {
        var label = _.i18n('pdfEditor.stampApproved', null, 'APPROVED');
        var text = new fabric.Text(label, {
          fontSize: 28, fill: '#16a34a', fontFamily: 'Arial', fontWeight: 'bold',
          originX: 'center', originY: 'center', left: 0, top: 0
        });
        var box = new fabric.Rect({
          width: text.width + 28, height: text.height + 16,
          fill: 'transparent', stroke: '#16a34a', strokeWidth: 3, rx: 6, ry: 6,
          originX: 'center', originY: 'center', left: 0, top: 0
        });
        obj = new fabric.Group([box, text], {
          left: cx, top: cy, originX: 'center', originY: 'center', angle: -12
        });
      }
      canvas.add(obj);
      canvas.setActiveObject(obj);
      canvas.requestRenderAll();
    },

    onInsertImage: function () {
      ImageSourcePickerView.open({
        title: _.i18n('imageSourcePicker.title', null, 'Insert image'),
        sources: this.options.imageSources || [],
        onPick: _.bind(this.insertImage, this)
      });
    },

    // opts.fit: 'page' scales the image to the largest size that fits the
    // artboard (used for product pictures); the default drops it in at half the
    // page size (used for logos and pasted / uploaded images).
    insertImage: function (url, opts) {
      if (!url || !this.fabricCanvas) { return; }
      var that = this;
      var fit = (opts && opts.fit) || 'half';
      this.recordUndo();

      this.resolveImageSource(url).then(function (src) {
        fabric.Image.fromURL(src.url, function (img) {
          // A cross-origin load blocked by the source server hands us a 0x0
          // image (or null). Don't drop an invisible, unselectable object on
          // the page — tell the user instead.
          if (!img || !img.width || !img.height) {
            that.undoStack.pop();
            that.updateHistoryButtons();
            that.notify(_.i18n('pdfEditor.imageInsertError', null,
              'That image could not be loaded — the source server may be blocking it.'));
            return;
          }

          var canvas = that.fabricCanvas;
          // Page size in canvas (unscaled) coordinates — getWidth()/getHeight()
          // return the zoom-scaled element size, which would misplace the image.
          var pageW = that._canvasW || canvas.getWidth();
          var pageH = that._canvasH || canvas.getHeight();
          var scale;
          if (fit === 'page') {
            // Largest size that still fits the artboard (with a small margin),
            // upscaling small images so the user never has to resize.
            scale = Math.min(pageW * 0.96 / img.width, pageH * 0.96 / img.height);
          } else {
            scale = Math.min(pageW * 0.5 / img.width, pageH * 0.5 / img.height, 1);
          }
          img.set({
            left: pageW / 2,
            top: pageH / 2,
            originX: 'center', originY: 'center',
            scaleX: scale, scaleY: scale
          });
          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.requestRenderAll();
        }, { crossOrigin: 'anonymous' });
      });
    },

    // Resolve any image URL to a self-contained data: URL before it becomes a
    // Fabric object. Two reasons:
    //  1. the order / product / customer proxy sends no CORS headers, so a
    //     `crossOrigin` <img> load fails and Fabric ends up with a 0x0 image;
    //  2. blob: URLs (uploads / paste) die with the page, so a saved proofsheet
    //     would re-open with broken images.
    // A data: URL sidesteps both — it never taints the export canvas and
    // round-trips through save / re-edit intact.
    resolveImageSource: function (url) {
      var toDataUrl = function (blob) {
        return new Promise(function (resolve, reject) {
          var fr = new FileReader();
          fr.onload = function () { resolve({ url: fr.result }); };
          fr.onerror = function () { reject(new Error('read failed')); };
          fr.readAsDataURL(blob);
        });
      };
      if (/^data:/i.test(url) || !window.fetch) { return Promise.resolve({ url: url }); }
      return window.fetch(url, { credentials: 'include' }).then(function (resp) {
        if (!resp.ok) { throw new Error('HTTP ' + resp.status); }
        return resp.blob();
      }).then(toDataUrl)['catch'](function () {
        return { url: url };
      });
    },

    deleteSelected: function () {
      var canvas = this.fabricCanvas;
      if (!canvas) { return; }
      var active = _.filter(canvas.getActiveObjects(), function (o) { return !o.locked; });
      if (!active.length) { return; }
      this.recordUndo();
      _.each(active, function (o) { canvas.remove(o); });
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    },

    /* --------------------------------------------------------------- */
    /* Right-click context menu                                         */
    /* --------------------------------------------------------------- */

    onContextMenu: function (e) {
      if (!this.fabricCanvas || document.querySelector('.image-editor-modal')) { return; }
      e.preventDefault();
      var canvas = this.fabricCanvas;
      var target = null;
      try { target = canvas.findTarget(e, false); } catch (err) { /* ignore */ }
      if (target && target !== canvas.getActiveObject()) {
        canvas.setActiveObject(target);
        canvas.requestRenderAll();
      }
      var active = canvas.getActiveObject();
      if (!active) { this.hideContextMenu(); return; }
      this.showContextMenu(e.clientX, e.clientY, active);
    },

    showContextMenu: function (clientX, clientY, obj) {
      var $menu = this.ui.ctxMenu;
      var modalEl = this.ui.modal[0];
      if (!$menu.length || !modalEl) { return; }
      this._ctxObj = obj;

      var selected = this.fabricCanvas.getActiveObjects();
      var single = (obj.type !== 'activeSelection') && selected.length <= 1;
      var anyLocked = _.some(selected, function (o) { return o.locked; });
      this.ui.ctxTransform.toggle(single && !anyLocked);
      this.ui.ctxOpacity.toggle(!anyLocked);
      if (single && !anyLocked) { this.syncCtxDims(obj); }
      if (selected.length && !anyLocked) { this.syncCtxAlpha(selected); }
      this.ui.ctxLockLabel.text(anyLocked ?
        _.i18n('pdfEditor.ctxUnlockItem', null, 'Unlock') :
        _.i18n('pdfEditor.ctxLockItem', null, 'Lock'));

      // Position within the modal (a transformed ancestor traps position:fixed).
      var rect = modalEl.getBoundingClientRect();
      $menu.css({ display: 'block', left: '-9999px', top: '0' });
      var mw = $menu.outerWidth();
      var mh = $menu.outerHeight();
      var x = clientX - rect.left;
      var y = clientY - rect.top;
      x = Math.max(4, Math.min(x, rect.width - mw - 4));
      y = Math.max(4, Math.min(y, rect.height - mh - 4));
      $menu.toggleClass('pe-ctx-flip', x + mw + 210 > rect.width);
      $menu.css({ left: x + 'px', top: y + 'px' });

      if (!this._ctxOutside) {
        this._ctxOutside = _.bind(function (ev) {
          if (!this.ui.ctxMenu[0].contains(ev.target)) { this.hideContextMenu(); }
        }, this);
      }
      var that = this;
      setTimeout(function () {
        document.addEventListener('mousedown', that._ctxOutside, true);
      }, 0);
    },

    hideContextMenu: function () {
      if (this.ui.ctxMenu && this.ui.ctxMenu.length) { this.ui.ctxMenu.hide(); }
      this._ctxObj = null;
      if (this._ctxOutside) {
        document.removeEventListener('mousedown', this._ctxOutside, true);
      }
    },

    // Object size (bounding box) in the page's currently selected unit.
    objSizeInUnit: function (obj) {
      var pxW = obj.getScaledWidth();
      var pxH = obj.getScaledHeight();
      var pg = this.pages[this.activeIndex];
      if (this.pageUnit === 'px' || !pg) { return { w: pxW, h: pxH }; }
      var f = 1 / (pg.scale * this.PT_PER_MM);
      return { w: pxW * f, h: pxH * f };
    },

    syncCtxDims: function (obj) {
      if (!obj) { return; }
      this.ui.ctxUnit.text(this.pageUnit);
      var s = this.objSizeInUnit(obj);
      this.ui.ctxW.val(Math.round(s.w * 10) / 10);
      this.ui.ctxH.val(Math.round(s.h * 10) / 10);
      this.ui.ctxLock.toggleClass('active', this.ctxLockAspect);
    },

    onCtxDimKey: function (e) {
      if (e.key === 'Enter') { e.preventDefault(); $(e.currentTarget).trigger('change').blur(); }
    },

    onCtxLockToggle: function () {
      this.ctxLockAspect = !this.ctxLockAspect;
      this.ui.ctxLock.toggleClass('active', this.ctxLockAspect);
    },

    onCtxDimChange: function (e) {
      var obj = this._ctxObj || (this.fabricCanvas && this.fabricCanvas.getActiveObject());
      var pg = this.pages[this.activeIndex];
      if (!obj || !pg) { return; }
      var editingH = $(e.currentTarget).hasClass('pe-ctx-h');
      var toPx = (this.pageUnit === 'px') ? 1 : (pg.scale * this.PT_PER_MM);
      var curW = obj.getScaledWidth();
      var curH = obj.getScaledHeight();
      var wv = parseFloat(this.ui.ctxW.val());
      var hv = parseFloat(this.ui.ctxH.val());
      var tgtW = (isFinite(wv) && wv > 0) ? wv * toPx : curW;
      var tgtH = (isFinite(hv) && hv > 0) ? hv * toPx : curH;

      this.recordUndo();
      if (this.ctxLockAspect) {
        var r = editingH ? (tgtH / curH) : (tgtW / curW);
        if (isFinite(r) && r > 0) { obj.scaleX *= r; obj.scaleY *= r; }
      } else {
        if (isFinite(tgtW / curW) && tgtW > 0) { obj.scaleX *= tgtW / curW; }
        if (isFinite(tgtH / curH) && tgtH > 0) { obj.scaleY *= tgtH / curH; }
      }
      obj.setCoords();
      obj.dirty = true;
      this.fabricCanvas.requestRenderAll();
      if (this.ui.ctxMenu.is(':visible')) { this.syncCtxDims(obj); }
    },

    // Transparency slider: 0% = opaque, 100% = fully see-through.
    syncCtxAlpha: function (objs) {
      var first = objs[0];
      var op = (first && typeof first.opacity === 'number') ? first.opacity : 1;
      var t = Math.round((1 - op) * 100);
      this.ui.ctxAlpha.val(t);
      this.ui.ctxAlphaVal.text(t + '%');
      this.ui.ctxAlphaBadge.text(t ? t + '%' : '');
    },

    ctxSelectedObjects: function () {
      var canvas = this.fabricCanvas;
      if (!canvas) { return []; }
      var objs = canvas.getActiveObjects();
      if (objs && objs.length) { return objs; }
      return this._ctxObj ? [this._ctxObj] : [];
    },

    onCtxAlphaInput: function () {
      var objs = this.ctxSelectedObjects();
      if (!objs.length) { return; }
      this._preEditJSON = this._preEditJSON || this.serializeObjects();
      var op = 1 - ((parseInt(this.ui.ctxAlpha.val(), 10) || 0) / 100);
      _.each(objs, function (o) { o.set('opacity', op); o.dirty = true; });
      var pct = this.ui.ctxAlpha.val();
      this.ui.ctxAlphaVal.text(pct + '%');
      this.ui.ctxAlphaBadge.text(parseInt(pct, 10) ? pct + '%' : '');
      this.fabricCanvas.requestRenderAll();
    },

    onCtxAlphaCommit: function () {
      this.onCtxAlphaInput();
      if (this._preEditJSON) {
        this.recordUndo(this._preEditJSON);
        this._preEditJSON = null;
      }
    },

    onContextAction: function (e) {
      var action = $(e.currentTarget).attr('data-ctx');
      var canvas = this.fabricCanvas;
      if (!canvas) { return; }
      var objs = canvas.getActiveObjects();
      var ctxObj = this._ctxObj || objs[0];
      this.hideContextMenu();
      if (!objs.length && !ctxObj) { return; }
      this.recordUndo();

      if (action === 'duplicate') {
        this.duplicateObjects(objs);
        return;
      }
      if (action === 'fit-obj-width' || action === 'fit-obj-height') {
        this.fitObjectToPage(ctxObj, action === 'fit-obj-width' ? 'width' : 'height');
        return;
      }
      if (action === 'lock') {
        var lock = !_.some(objs, function (o) { return o.locked; });
        var self = this;
        _.each(objs, function (o) { self.applyLock(o, lock); });
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        return;
      }
      _.each(objs, function (o) {
        if (action === 'front') { canvas.bringToFront(o); }
        else if (action === 'forward') { canvas.bringForward(o); }
        else if (action === 'backward') { canvas.sendBackwards(o); }
        else if (action === 'back') { canvas.sendToBack(o); }
      });
      canvas.discardActiveObject();
      if (objs.length === 1) { canvas.setActiveObject(objs[0]); }
      canvas.requestRenderAll();
    },

    // Scale the object so its bounding box fills the page on the given axis
    // (keeping the aspect ratio when the lock is on) and centre it on that axis.
    fitObjectToPage: function (obj, axis) {
      var canvas = this.fabricCanvas;
      if (!obj || !canvas || obj.locked) { return; }
      var pageW = this._canvasW || canvas.getWidth();
      var pageH = this._canvasH || canvas.getHeight();
      var factor = (axis === 'width') ?
        (pageW / (obj.getScaledWidth() || 1)) :
        (pageH / (obj.getScaledHeight() || 1));
      if (!isFinite(factor) || factor <= 0) { return; }

      if (this.ctxLockAspect) {
        obj.scaleX *= factor;
        obj.scaleY *= factor;
      } else if (axis === 'width') {
        obj.scaleX *= factor;
      } else {
        obj.scaleY *= factor;
      }

      var c = obj.getCenterPoint();
      obj.setPositionByOrigin(
        new fabric.Point(axis === 'width' ? pageW / 2 : c.x, axis === 'height' ? pageH / 2 : c.y),
        'center', 'center');
      obj.setCoords();
      obj.dirty = true;
      canvas.requestRenderAll();
    },

    // Freeze / release an object: locked objects can still be clicked (so they
    // can be unlocked from this menu) but not moved, scaled, rotated or deleted.
    applyLock: function (obj, locked) {
      obj.locked = !!locked;
      obj.set({
        lockMovementX: !!locked, lockMovementY: !!locked,
        lockScalingX: !!locked, lockScalingY: !!locked,
        lockRotation: !!locked, lockSkewingX: !!locked, lockSkewingY: !!locked,
        hasControls: !locked,
        hoverCursor: locked ? 'default' : null
      });
      obj.setCoords();
    },

    duplicateObjects: function (objs) {
      var canvas = this.fabricCanvas;
      var that = this;
      canvas.discardActiveObject();
      var clones = [];
      var pending = objs.length;
      _.each(objs, function (o) {
        o.clone(function (c) {
          c.set({ left: (c.left || 0) + 24, top: (c.top || 0) + 24, evented: true });
          that.applyLock(c, false);   // a fresh copy is never locked
          if (c.meshActive || c.warpActive) { that.ensureWarpRender(c); }
          canvas.add(c);
          clones.push(c);
          pending--;
          if (pending === 0) {
            if (clones.length === 1) {
              canvas.setActiveObject(clones[0]);
            } else {
              canvas.setActiveObject(new fabric.ActiveSelection(clones, { canvas: canvas }));
            }
            canvas.requestRenderAll();
          }
        }, CUSTOM_JSON_PROPS);
      });
    },

    /* --------------------------------------------------------------- */
    /* Properties panel                                                 */
    /* --------------------------------------------------------------- */

    onSelection: function () {
      var obj = this.fabricCanvas.getActiveObject();
      if (!obj) { return; }
      if (obj.type === 'textbox' || obj.type === 'text') {
        if (obj.fill) { this.ui.strokeColor.val(this.toHex(obj.fill)); }
        if (obj.fontSize) { this.ui.fontSize.val(obj.fontSize); }
      } else if (obj.stroke) {
        this.ui.strokeColor.val(this.toHex(obj.stroke));
        if (obj.strokeWidth) { this.ui.strokeWidth.val(obj.strokeWidth); }
      }
      // Reflect the selected shape's interior fill into the Fill control.
      if (_.contains(FILLABLE_TYPES, obj.type)) {
        var hasFill = !!obj.fill && obj.fill !== 'transparent' &&
          !/rgba?\([^)]*,\s*0\s*\)$/.test(String(obj.fill));
        this.ui.fillOn.prop('checked', hasFill);
        this.ui.fillColor.prop('disabled', !hasFill);
        if (hasFill) { this.ui.fillColor.val(this.toHex(obj.fill)); }
      }
      this.refreshWarpToolbar();
    },

    onStrokeColorChange: function () {
      this.strokeColor = this.ui.strokeColor.val();
      if (this.fabricCanvas) {
        this.fabricCanvas.freeDrawingBrush.color = this.strokeColor;
        var obj = this.fabricCanvas.getActiveObject();
        if (obj) {
          if (obj.type === 'textbox' || obj.type === 'text') { obj.set('fill', this.strokeColor); }
          else { obj.set('stroke', this.strokeColor); }
          this.fabricCanvas.requestRenderAll();
        }
      }
    },

    onStrokeWidthChange: function () {
      this.strokeWidth = parseInt(this.ui.strokeWidth.val(), 10) || 1;
      if (this.fabricCanvas) {
        this.fabricCanvas.freeDrawingBrush.width = this.strokeWidth;
        var obj = this.fabricCanvas.getActiveObject();
        if (obj && obj.stroke && obj.type !== 'textbox' && obj.type !== 'text') {
          obj.set('strokeWidth', this.strokeWidth);
          this.fabricCanvas.requestRenderAll();
        }
      }
    },

    // Toggles / recolors the interior fill of the active closed shape (and sets
    // the default for the next Box drawn). Unchecking the box makes the fill
    // transparent again.
    onFillChange: function () {
      this.fillEnabled = this.ui.fillOn.is(':checked');
      this.fillColor = this.ui.fillColor.val();
      this.ui.fillColor.prop('disabled', !this.fillEnabled);
      if (this.fabricCanvas) {
        var obj = this.fabricCanvas.getActiveObject();
        if (obj && _.contains(FILLABLE_TYPES, obj.type)) {
          obj.set('fill', this.fillEnabled ? this.fillColor : 'transparent');
          this.fabricCanvas.requestRenderAll();
        }
      }
    },

    onFontSizeChange: function () {
      this.fontSize = parseInt(this.ui.fontSize.val(), 10) || 12;
      if (this.fabricCanvas) {
        var obj = this.fabricCanvas.getActiveObject();
        if (obj && (obj.type === 'textbox' || obj.type === 'text')) {
          obj.set('fontSize', this.fontSize);
          this.fabricCanvas.requestRenderAll();
        }
      }
    },

    /* --------------------------------------------------------------- */
    /* Page (artwork) size                                              */
    /* --------------------------------------------------------------- */

    // PDF points per millimetre (1pt = 1/72 inch, 1in = 25.4mm).
    PT_PER_MM: 72 / 25.4,

    // The active page's size expressed in the selected unit. px are the page's
    // own canvas pixels (baseW/baseH); mm is derived from the point size.
    pageSizeInUnit: function (pg) {
      if (this.pageUnit === 'px') {
        return { w: pg.baseW, h: pg.baseH };
      }
      return { w: pg.ptW / this.PT_PER_MM, h: pg.ptH / this.PT_PER_MM };
    },

    // Reflect the active page's dimensions into the toolbar inputs.
    syncPageSizeInputs: function () {
      if (!this.ui.pageW || !this.ui.pageW.length) { return; }
      var pg = this.pages[this.activeIndex];
      if (!pg) { this.ui.pageW.val(''); this.ui.pageH.val(''); return; }
      var s = this.pageSizeInUnit(pg);
      this.ui.pageW.val(Math.round(s.w * 10) / 10);
      this.ui.pageH.val(Math.round(s.h * 10) / 10);
    },

    onPageUnitChange: function () {
      this.pageUnit = (this.ui.pageUnit.val() === 'px') ? 'px' : 'mm';
      this.syncPageSizeInputs();
    },

    onPageSizeChange: function () {
      var pg = this.pages[this.activeIndex];
      if (!pg) { return; }
      var wv = parseFloat(this.ui.pageW.val());
      var hv = parseFloat(this.ui.pageH.val());
      if (!isFinite(wv) || wv <= 0 || !isFinite(hv) || hv <= 0) {
        this.syncPageSizeInputs();
        return;
      }
      // Convert the entered size to PDF points (the page's intrinsic unit).
      var newPtW, newPtH;
      if (this.pageUnit === 'px') {
        newPtW = wv / pg.scale;
        newPtH = hv / pg.scale;
      } else {
        newPtW = wv * this.PT_PER_MM;
        newPtH = hv * this.PT_PER_MM;
      }
      this.setPageSize(pg, newPtW, newPtH);
    },

    // Resize the page to newPtW × newPtH points, scaling its background bitmap
    // and every overlay object by the same factors so the whole artwork grows
    // or shrinks with the page. Resized pages export via the raster path (like
    // rotated pages) so the new size is always honoured — the original vector
    // media box cannot be rescaled cheaply.
    setPageSize: function (pg, newPtW, newPtH) {
      var idx = this.pages.indexOf(pg);
      if (idx === this.activeIndex) { this.saveCurrentPageOverlay(); }

      var newBaseW = Math.max(1, Math.round(newPtW * pg.scale));
      var newBaseH = Math.max(1, Math.round(newPtH * pg.scale));
      var fx = newBaseW / pg.baseW;
      var fy = newBaseH / pg.baseH;

      var objects = (pg.overlayJSON && pg.overlayJSON.objects) ? pg.overlayJSON.objects : [];
      _.each(objects, function (o) {
        if (typeof o.left === 'number') { o.left *= fx; }
        if (typeof o.top === 'number') { o.top *= fy; }
        o.scaleX = (o.scaleX || 1) * fx;
        o.scaleY = (o.scaleY || 1) * fy;
      });

      pg.ptW = newPtW;
      pg.ptH = newPtH;
      pg.baseW = newBaseW;
      pg.baseH = newBaseH;
      pg.resized = true;

      this.activatePage(idx, true);
      this.syncPageSizeInputs();
    },

    // Trim (or extend) the page height so it matches the vertical extent of the
    // content — the width is left untouched. Content is nudged up so it starts
    // at the top, and the page background bitmap is cropped to the same slice.
    fitPageHeightToContent: function () {
      var pg = this.pages[this.activeIndex];
      var canvas = this.fabricCanvas;
      if (!pg || !canvas) { return; }
      if ((pg.rotation || 0) % 360 !== 0) {
        this.notify(_.i18n('pdfEditor.fitHeightRotated', null,
          'Reset the page rotation first, then fit the height.'));
        return;
      }
      var objects = canvas.getObjects();
      if (!objects.length) {
        this.notify(_.i18n('pdfEditor.fitHeightEmpty', null,
          'Add an image or object to the page first.'));
        return;
      }

      var minTop = Infinity, maxBottom = -Infinity;
      _.each(objects, function (o) {
        o.setCoords();
        var r = o.getBoundingRect(true, true); // scene coords, recalculated
        if (r.top < minTop) { minTop = r.top; }
        if (r.top + r.height > maxBottom) { maxBottom = r.top + r.height; }
      });
      if (!isFinite(minTop) || !isFinite(maxBottom)) { return; }

      minTop = Math.max(0, Math.round(minTop));
      maxBottom = Math.round(maxBottom);
      var newBaseH = maxBottom - minTop;
      if (newBaseH < 8) { return; }
      if (minTop === 0 && Math.abs(newBaseH - pg.baseH) <= 2) { return; } // already fitted

      var that = this;
      this.saveCurrentPageOverlay();
      this.buildFittedBackground(pg, minTop, newBaseH).then(function (bgUrl) {
        var objs = (pg.overlayJSON && pg.overlayJSON.objects) ? pg.overlayJSON.objects : [];
        _.each(objs, function (o) {
          if (typeof o.top === 'number') { o.top -= minTop; }
        });
        pg.baseDataUrl = bgUrl;
        pg.baseH = newBaseH;
        pg.ptH = newBaseH / pg.scale;
        pg.resized = true;
        pg.rasterEdited = true;
        // A page-geometry change moves every object; the object-level history
        // entries no longer line up, so start a fresh history from here.
        that.activatePage(that.activeIndex, false);
        that.syncPageSizeInputs();
      });
    },

    // Crop the page background bitmap to the vertical slice [cropTop, cropTop +
    // cropH] (canvas-pixel units). If the slice runs past the original bitmap
    // (the page is being extended) the extra strip is filled white.
    buildFittedBackground: function (pg, cropTop, cropH) {
      return new Promise(function (resolve) {
        var image = new Image();
        image.onload = function () {
          var ratio = (pg.baseH > 0) ? (image.naturalHeight / pg.baseH) : 1;
          var sy = cropTop * ratio;
          var sh = cropH * ratio;
          var out = document.createElement('canvas');
          out.width = image.naturalWidth || 1;
          out.height = Math.max(1, Math.round(sh));
          var ctx = out.getContext('2d');

          var vTop = Math.max(0, sy);
          var vBottom = Math.min(image.naturalHeight, sy + sh);
          if (vBottom > vTop) {
            ctx.drawImage(image,
              0, vTop, image.naturalWidth, vBottom - vTop,
              0, Math.round(vTop - sy), out.width, Math.round(vBottom - vTop));
          }
          ctx.fillStyle = '#ffffff';
          if (sy < 0) { ctx.fillRect(0, 0, out.width, Math.round(-sy)); }
          if (sy + sh > image.naturalHeight) {
            var gap = Math.round((sy + sh) - image.naturalHeight);
            ctx.fillRect(0, out.height - gap, out.width, gap);
          }
          resolve(out.toDataURL('image/png'));
        };
        image.onerror = function () { resolve(pg.baseDataUrl); };
        image.src = pg.baseDataUrl;
      });
    },

    /* --------------------------------------------------------------- */
    /* Undo                                                             */
    /* --------------------------------------------------------------- */

    serializeObjects: function () {
      var json = this.fabricCanvas.toJSON(CUSTOM_JSON_PROPS);
      return JSON.stringify(json.objects || []);
    },

    // Record a history checkpoint. Any real edit calls this, which also drops
    // the redo stack (a new action forks history).
    recordUndo: function (json) {
      if (!this.fabricCanvas) { return; }
      this.undoStack.push(json || this.serializeObjects());
      if (this.undoStack.length > 80) { this.undoStack.shift(); }
      this.redoStack = [];
      this.updateHistoryButtons();
    },

    undo: function () {
      if (!this.fabricCanvas || !this.undoStack.length) { return; }
      this.redoStack.push(this.serializeObjects());
      this.applyHistoryState(this.undoStack.pop());
    },

    redo: function () {
      if (!this.fabricCanvas || !this.redoStack.length) { return; }
      this.undoStack.push(this.serializeObjects());
      this.applyHistoryState(this.redoStack.pop());
    },

    applyHistoryState: function (json) {
      var canvas = this.fabricCanvas;
      var bg = canvas.backgroundImage;
      var that = this;
      canvas.loadFromJSON({ objects: JSON.parse(json) }, function () {
        that.restoreWarpedImages(canvas);
        canvas.setBackgroundImage(bg, canvas.requestRenderAll.bind(canvas));
        that.updateHistoryButtons();
      });
    },

    updateHistoryButtons: function () {
      this.$('[data-action="undo"]').toggleClass('pe-disabled', !this.undoStack.length);
      this.$('[data-action="redo"]').toggleClass('pe-disabled', !this.redoStack.length);
    },

    /* --------------------------------------------------------------- */
    /* Free Transform & Warp                                           */
    /* --------------------------------------------------------------- */

    // Toggle: allows dragging any corner handle to scale X and Y independently
    // (instead of proportionally). A second click restores proportional scaling.
    toggleFreeTransform: function () {
      this.freeTransform = !this.freeTransform;
      this.$('[data-action="free-transform"]').toggleClass('active', this.freeTransform);
      if (this.fabricCanvas) {
        this.fabricCanvas.uniformScaling = !this.freeTransform;
      }
    },

    // Toggle 4-corner perspective warp on the selected image. The 8 scale
    // handles are replaced with 4 magenta corner handles; dragging one pulls
    // that corner independently (keystone / bow). A second click removes it.
    toggleWarp: function () {
      var canvas = this.fabricCanvas;
      if (!canvas) { return; }
      var img = canvas.getActiveObject();
      if (!img || img.type !== 'image') { return; }
      this.recordUndo();
      if (img.warpActive) {
        this.disableImageWarp(img);
      } else {
        this.disableImageWarp(img);              // clear any mesh warp first
        img.warpActive = true;
        img.warpOffsets = { tl:{x:0,y:0}, tr:{x:0,y:0}, br:{x:0,y:0}, bl:{x:0,y:0} };
        this.ensureWarpRender(img);
        this.applyCornerWarpControls(img);
      }
      this.refreshWarpToolbar();
      img.dirty = true;
      canvas.requestRenderAll();
    },

    // Toggle the free-form mesh grid handles on the selected image. The curve
    // presets (Style panel) turn the mesh render on without showing handles;
    // this button is for hand-tweaking individual grid points.
    toggleMeshWarp: function () {
      var canvas = this.fabricCanvas;
      if (!canvas) { return; }
      var img = canvas.getActiveObject();
      if (!img || img.type !== 'image') { return; }
      this.recordUndo();

      if (img.meshEditing) {
        this.hideMeshControls(img);
        // A mesh that was never actually deformed is dropped entirely.
        if (this.meshIsFlat(img.meshWarp)) { this.disableImageWarp(img); }
      } else {
        if (img.warpActive) {
          img.warpActive = false;
          img.warpOffsets = null;
        }
        if (!img.meshWarp) { img.meshWarp = identityMesh(MESH_ROWS, MESH_COLS); }
        img.meshActive = true;
        this.ensureWarpRender(img);
        this.showMeshControls(img);
      }
      this.refreshWarpToolbar();
      img.dirty = true;
      canvas.requestRenderAll();
    },

    // Live update while the Curve slider / dropdown is being changed.
    onCurveInput: function () {
      var img = this.fabricCanvas && this.fabricCanvas.getActiveObject();
      if (!img || img.type !== 'image') { return; }
      this._preEditJSON = this._preEditJSON || this.serializeObjects();
      this.applyCurveFromInputs(img);
    },

    // Commit the curve change to the undo stack (fires on slider release / select
    // change).
    onCurveCommit: function () {
      var img = this.fabricCanvas && this.fabricCanvas.getActiveObject();
      if (!img || img.type !== 'image') { return; }
      if (!this._preEditJSON) { this._preEditJSON = this.serializeObjects(); }
      this.applyCurveFromInputs(img);
      this.recordUndo(this._preEditJSON);
      this._preEditJSON = null;
    },

    applyCurveFromInputs: function (img) {
      var type = this.ui.curveType.val();
      var amount = (parseInt(this.ui.curveAmount.val(), 10) || 0) / 100;
      this.ui.curveAmount.prop('disabled', !type);
      if (!type || !amount) {
        this.disableImageWarp(img);
      } else {
        if (img.warpActive) { img.warpActive = false; img.warpOffsets = null; }
        img.meshWarp = presetMesh(MESH_ROWS, MESH_COLS, img.width, img.height, type, amount);
        img.meshActive = true;
        this.ensureWarpRender(img);
      }
      this.refreshWarpToolbar(false);
      img.dirty = true;
      this.fabricCanvas.requestRenderAll();
    },

    meshIsFlat: function (mesh) {
      if (!mesh || !mesh.points) { return true; }
      for (var r = 0; r < mesh.points.length; r++) {
        for (var c = 0; c < mesh.points[r].length; c++) {
          var p = mesh.points[r][c];
          if (p && (Math.abs(p.x) > 0.5 || Math.abs(p.y) > 0.5)) { return false; }
        }
      }
      return true;
    },

    // Turn every warp off and restore the plain image render + standard controls.
    disableImageWarp: function (img) {
      img.warpActive = false;
      img.warpOffsets = null;
      img.meshActive = false;
      img.meshWarp = null;
      img.meshEditing = false;
      img.objectCaching = true;
      img._render = fabric.Image.prototype._render;
      if (img._originalControls) {
        img.controls = img._originalControls;
        img._originalControls = null;
      }
    },

    // Install the shared _render override that routes an image through whichever
    // warp is active. Idempotent. Warped images skip Fabric's object cache so
    // content that bows outside the original bounding box isn't clipped.
    ensureWarpRender: function (img) {
      img.objectCaching = false;
      img._render = function (ctx) {
        var el = this._element;
        var iw = this.width, ih = this.height;
        var cx = this.cropX || 0, cy = this.cropY || 0;
        if (el && iw && ih && this.meshActive && this.meshWarp) {
          renderMeshImage(ctx, el, iw, ih, cx, cy, this.meshWarp);
        } else if (el && iw && ih && this.warpActive && this.warpOffsets) {
          renderWarpedImage(ctx, el, iw, ih, cx, cy, this.warpOffsets);
        } else {
          fabric.Image.prototype._render.call(this, ctx);
        }
      };
    },

    applyCornerWarpControls: function (img) {
      var that = this;
      var CORNERS = { tl: {x:-0.5, y:-0.5}, tr: {x:0.5, y:-0.5},
                      br: {x:0.5, y:0.5},   bl: {x:-0.5, y:0.5} };
      var controls = {};
      if (!img._originalControls) { img._originalControls = img.controls; }

      _.each(CORNERS, function (base, corner) {
        controls[corner] = new fabric.Control({
          x: base.x, y: base.y,
          cursorStyle: 'crosshair',
          actionName: 'warp',
          render: function (ctx, left, top) { that.drawWarpHandle(ctx, left, top, true); },
          positionHandler: function (dim, finalMatrix, o) {
            var off = (o.warpOffsets && o.warpOffsets[corner]) || { x:0, y:0 };
            var kx = o.width > 0 ? dim.x / o.width : 1;
            var ky = o.height > 0 ? dim.y / o.height : 1;
            return fabric.util.transformPoint(
              { x: base.x * dim.x + off.x * kx, y: base.y * dim.y + off.y * ky }, finalMatrix);
          },
          actionHandler: function (eventData, transform, x, y) {
            var t = transform.target;
            if (!t || !eventData) { return false; }
            var local = fabric.util.transformPoint({ x: x, y: y },
              fabric.util.invertTransform(t.calcTransformMatrix()));
            if (!t.warpOffsets) {
              t.warpOffsets = { tl:{x:0,y:0}, tr:{x:0,y:0}, br:{x:0,y:0}, bl:{x:0,y:0} };
            }
            t.warpOffsets[corner] = { x: local.x - base.x * t.width, y: local.y - base.y * t.height };
            t.dirty = true;
            that._preEditJSON = that._preEditJSON || that.serializeObjects();
            return true;
          }
        });
      });
      img.controls = controls;
    },

    showMeshControls: function (img) {
      var that = this;
      var mesh = img.meshWarp;
      if (!mesh) { return; }
      if (!img._originalControls) { img._originalControls = img.controls; }
      var controls = {};
      var R = mesh.rows, C = mesh.cols;

      var make = function (r, c) {
        var bx = c / C - 0.5, by = r / R - 0.5;
        var isCorner = (r === 0 || r === R) && (c === 0 || c === C);
        return new fabric.Control({
          x: bx, y: by,
          cursorStyle: 'crosshair',
          actionName: 'meshwarp',
          render: function (ctx, left, top) { that.drawWarpHandle(ctx, left, top, isCorner); },
          positionHandler: function (dim, finalMatrix, o) {
            var mw = o.meshWarp;
            var off = (mw && mw.points[r] && mw.points[r][c]) || { x:0, y:0 };
            var kx = o.width > 0 ? dim.x / o.width : 1;
            var ky = o.height > 0 ? dim.y / o.height : 1;
            return fabric.util.transformPoint(
              { x: bx * dim.x + off.x * kx, y: by * dim.y + off.y * ky }, finalMatrix);
          },
          actionHandler: function (eventData, transform, x, y) {
            var t = transform.target;
            if (!t || !t.meshWarp || !eventData) { return false; }
            var local = fabric.util.transformPoint({ x: x, y: y },
              fabric.util.invertTransform(t.calcTransformMatrix()));
            if (!t.meshWarp.points[r]) { t.meshWarp.points[r] = []; }
            t.meshWarp.points[r][c] = { x: local.x - bx * t.width, y: local.y - by * t.height };
            t.meshWarp.preset = '';   // hand-edited — no longer a pure preset
            t.dirty = true;
            that._preEditJSON = that._preEditJSON || that.serializeObjects();
            return true;
          }
        });
      };

      for (var r = 0; r <= R; r++) {
        for (var c = 0; c <= C; c++) { controls['m_' + r + '_' + c] = make(r, c); }
      }
      img.controls = controls;
      img.meshEditing = true;
    },

    hideMeshControls: function (img) {
      img.meshEditing = false;
      if (img._originalControls) {
        img.controls = img._originalControls;
        img._originalControls = null;
      }
    },

    drawWarpHandle: function (ctx, left, top, isCorner) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(left, top, isCorner ? 6 : 5, 0, Math.PI * 2);
      ctx.fillStyle = isCorner ? '#cc00ff' : '#00b3b3';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    },

    // Light up the Warp / Mesh toolbar buttons for the current selection.
    // `syncCurveInputs` is skipped while the user is actively dragging the Curve
    // slider (writing the value back mid-drag would fight the interaction).
    refreshWarpToolbar: function (syncCurveInputs) {
      var img = this.fabricCanvas && this.fabricCanvas.getActiveObject();
      var isImg = !!img && img.type === 'image';
      this.$('[data-action="warp"]').toggleClass('active', isImg && !!img.warpActive);
      this.$('[data-action="mesh-warp"]').toggleClass('active', isImg && !!img.meshEditing);
      if (syncCurveInputs !== false && this.ui.curveType && this.ui.curveType.length) {
        var mw = isImg ? img.meshWarp : null;
        this.ui.curveType.val((mw && mw.preset) || '');
        this.ui.curveAmount.val(mw && mw.preset ? Math.round((mw.amount || 0) * 100) : 0);
        this.ui.curveAmount.prop('disabled', !(mw && mw.preset));
      }
    },

    // After any JSON round-trip re-attach the warp render override (and, in the
    // live editor, the grid handles if the image was being mesh-edited).
    restoreWarpedImages: function (canvas) {
      var that = this;
      var live = (canvas === this.fabricCanvas);
      _.each(canvas.getObjects(), function (obj) {
        // fabric doesn't serialize the lock* flags, so re-apply them from our
        // own `locked` marker after every load.
        if (obj.locked) { that.applyLock(obj, true); }
        if (obj.type !== 'image') { return; }
        if (obj.meshActive && obj.meshWarp) {
          that.ensureWarpRender(obj);
          if (live && obj.meshEditing) { that.showMeshControls(obj); }
        } else if (obj.warpActive && obj.warpOffsets) {
          that.ensureWarpRender(obj);
          if (live) { that.applyCornerWarpControls(obj); }
        }
      });
    },

    /* --------------------------------------------------------------- */
    /* Per-page raster edit                                             */
    /* --------------------------------------------------------------- */

    rasterEditPage: function (index) {
      var pg = this.pages[index];
      var that = this;
      // Edit the natural-orientation page bitmap; rotation is re-applied on display.
      ImageEditorView.open({
        url: pg.baseDataUrl,
        fileName: 'page-' + (index + 1) + '.png',
        title: _.i18n('pdfEditor.rasterEdit', null, 'Edit image'),
        onSave: function (file) {
          var reader = new FileReader();
          reader.onload = function () {
            pg.baseDataUrl = reader.result;
            pg.rasterEdited = true;
            that.activatePage(index, true);
          };
          reader.readAsDataURL(file);
        }
      });
    },

    /* --------------------------------------------------------------- */
    /* Decompose: make existing text & images editable (best-effort)    */
    /* --------------------------------------------------------------- */

    decomposePage: function (index) {
      var pg = this.pages[index];
      if (!pg || !pg.pdfPage) { return; }
      if (pg.decomposed) {
        this.notify(_.i18n('pdfEditor.alreadyExtracted', null,
          'This page is already broken into editable objects.'));
        return;
      }
      var that = this;
      if (this.activeIndex === index) { this.saveCurrentPageOverlay(); }
      this.showLoader(true);

      var viewport = pg.pdfPage.getViewport({ scale: pg.scale });

      this.loadImageEl(pg.baseDataUrl).then(function (baseImg) {
        var sampleCanvas = document.createElement('canvas');
        sampleCanvas.width = pg.baseW;
        sampleCanvas.height = pg.baseH;
        var sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
        sampleCtx.drawImage(baseImg, 0, 0, pg.baseW, pg.baseH);

        var maskCanvas = document.createElement('canvas');
        maskCanvas.width = pg.baseW;
        maskCanvas.height = pg.baseH;
        var maskCtx = maskCanvas.getContext('2d');
        maskCtx.drawImage(baseImg, 0, 0, pg.baseW, pg.baseH);

        var measureCtx = document.createElement('canvas').getContext('2d');

        return pg.pdfPage.getTextContent().then(function (textContent) {
          var textRes = that.buildTextObjects(textContent, viewport, sampleCtx, measureCtx);
          return pg.pdfPage.getOperatorList().then(function (opList) {
            var imgRes = that.buildImageObjects(opList, viewport, pg);

            // Erase the originals from the background so moved/edited objects
            // don't leave a ghost behind them.
            var boxes = textRes.boxes.concat(imgRes.boxes);
            _.each(boxes, function (box) {
              var color = that.sampleHex(sampleCtx, box.x - 2, box.y - 2) ||
                that.sampleHex(sampleCtx, box.x + box.w + 2, box.y - 2) || '#ffffff';
              maskCtx.fillStyle = color;
              maskCtx.fillRect(Math.floor(box.x), Math.floor(box.y),
                Math.ceil(box.w), Math.ceil(box.h));
            });

            // Keep any existing annotations underneath; add extracted images
            // then text on top.
            var existing = (pg.overlayJSON && pg.overlayJSON.objects) ?
              pg.overlayJSON.objects.slice() : [];
            pg.overlayJSON = { objects: existing.concat(imgRes.objects).concat(textRes.objects) };
            pg.baseDataUrl = maskCanvas.toDataURL('image/png');
            pg.rasterEdited = true;
            pg.decomposed = true;

            that.showLoader(false);
            that.activatePage(index, true);
          });
        });
      })['catch'](function (err) {
        that.showLoader(false);
        if (window.console) { console.error('PDF decompose failed', err); }
        that.notify(_.i18n('pdfEditor.extractError', null,
          'Could not extract editable content from this page.'));
      });
    },

    buildTextObjects: function (textContent, viewport, sampleCtx, measureCtx) {
      var Util = getPdfjs().Util;
      var styles = textContent.styles || {};
      var objects = [];
      var boxes = [];

      _.each(textContent.items, function (item) {
        var str = item.str;
        if (!str || !str.replace(/\s/g, '').length) { return; }
        var tx = Util.transform(viewport.transform, item.transform);
        var fontSize = Math.hypot(tx[2], tx[3]);
        if (!fontSize || !isFinite(fontSize) || fontSize < 1) { return; }
        var angle = Math.atan2(tx[1], tx[0]) * 180 / Math.PI;
        var left = tx[4];
        var baseline = tx[5];
        var top = baseline - fontSize;
        var fontFamily = this.normalizeFont((styles[item.fontName] || {}).fontFamily);

        measureCtx.font = fontSize + 'px ' + fontFamily;
        var wdev = measureCtx.measureText(str).width || (fontSize * str.length * 0.5);
        var fill = this.sampleHex(sampleCtx, left + wdev / 2, baseline - fontSize / 3) || '#000000';

        objects.push({
          type: 'i-text',
          text: str,
          left: left,
          top: top,
          fontSize: fontSize,
          fill: fill,
          fontFamily: fontFamily,
          angle: angle,
          originX: 'left',
          originY: 'top'
        });
        boxes.push({ x: left - 1, y: top - 1, w: wdev + 2, h: fontSize * 1.3 });
      }, this);

      return { objects: objects, boxes: boxes };
    },

    buildImageObjects: function (opList, viewport, pg) {
      var pdfjsLib = getPdfjs();
      var OPS = pdfjsLib.OPS;
      var Util = pdfjsLib.Util;
      var fnArray = opList.fnArray;
      var argsArray = opList.argsArray;
      var ctm = [1, 0, 0, 1, 0, 0];
      var stack = [];
      var objects = [];
      var boxes = [];

      for (var i = 0; i < fnArray.length; i++) {
        var fn = fnArray[i];
        if (fn === OPS.save) {
          stack.push(ctm.slice());
        } else if (fn === OPS.restore) {
          ctm = stack.pop() || [1, 0, 0, 1, 0, 0];
        } else if (fn === OPS.transform) {
          ctm = Util.transform(ctm, argsArray[i]);
        } else if (fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject) {
          this.pushImage(objects, boxes, this.resolveImageObj(pg, argsArray[i][0]), ctm, viewport);
        } else if (fn === OPS.paintInlineImage) {
          this.pushImage(objects, boxes, argsArray[i][0], ctm, viewport);
        }
      }
      return { objects: objects, boxes: boxes };
    },

    resolveImageObj: function (pg, name) {
      try {
        if (pg.pdfPage.objs.has(name)) { return pg.pdfPage.objs.get(name); }
      } catch (e1) { /* not in objs */ }
      try {
        if (pg.pdfPage.commonObjs.has(name)) { return pg.pdfPage.commonObjs.get(name); }
      } catch (e2) { /* not in commonObjs */ }
      return null;
    },

    pushImage: function (objects, boxes, imgObj, ctm, viewport) {
      if (!imgObj) { return; }
      var dataUrl = this.imgObjToDataUrl(imgObj);
      if (!dataUrl) { return; }

      var dm = getPdfjs().Util.transform(viewport.transform, ctm);
      var apply = function (x, y) {
        return { x: dm[0] * x + dm[2] * y + dm[4], y: dm[1] * x + dm[3] * y + dm[5] };
      };
      var pts = [apply(0, 0), apply(1, 0), apply(0, 1), apply(1, 1)];
      var xs = _.pluck(pts, 'x');
      var ys = _.pluck(pts, 'y');
      var left = Math.min.apply(null, xs);
      var top = Math.min.apply(null, ys);
      var w = Math.max.apply(null, xs) - left;
      var h = Math.max.apply(null, ys) - top;
      if (!isFinite(w) || !isFinite(h) || w < 2 || h < 2) { return; }

      objects.push({
        type: 'image',
        src: dataUrl,
        left: left,
        top: top,
        originX: 'left',
        originY: 'top',
        scaleX: w / imgObj.width,
        scaleY: h / imgObj.height,
        angle: 0,
        crossOrigin: 'anonymous'
      });
      boxes.push({ x: left, y: top, w: w, h: h });
    },

    imgObjToDataUrl: function (imgObj) {
      try {
        var w = imgObj.width;
        var h = imgObj.height;
        if (!w || !h) { return null; }

        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext('2d');

        var data = imgObj.data;
        if (!data) {
          // Already a drawable bitmap (canvas / image / ImageBitmap).
          ctx.drawImage(imgObj, 0, 0);
          return canvas.toDataURL('image/png');
        }

        var imageData = ctx.createImageData(w, h);
        var dst = imageData.data;
        var IK = getPdfjs().ImageKind || {};
        var n = w * h;
        var i, j;

        if (imgObj.kind === (IK.RGB_24BPP || 2) && data.length >= n * 3) {
          for (i = 0, j = 0; i < n; i++) {
            dst[j++] = data[i * 3];
            dst[j++] = data[i * 3 + 1];
            dst[j++] = data[i * 3 + 2];
            dst[j++] = 255;
          }
        } else if (imgObj.kind === (IK.GRAYSCALE_1BPP || 1)) {
          var rowBytes = (w + 7) >> 3;
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              var bval = data[y * rowBytes + (x >> 3)] || 0;
              var v = ((bval >> (7 - (x & 7))) & 1) ? 255 : 0;
              var o = (y * w + x) * 4;
              dst[o] = v; dst[o + 1] = v; dst[o + 2] = v; dst[o + 3] = 255;
            }
          }
        } else if (data.length >= n * 4) {
          dst.set(data.length === n * 4 ? data : data.subarray(0, n * 4));
        } else {
          return null;
        }
        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL('image/png');
      } catch (e) {
        return null;
      }
    },

    normalizeFont: function (family) {
      if (!family) { return 'Arial'; }
      var f = family.toLowerCase();
      if (f.indexOf('mono') !== -1) { return 'monospace'; }
      if (f.indexOf('serif') !== -1 && f.indexOf('sans') === -1) { return 'serif'; }
      return 'Arial';
    },

    sampleHex: function (ctx, x, y) {
      try {
        var cx = Math.max(0, Math.min(ctx.canvas.width - 1, Math.round(x)));
        var cy = Math.max(0, Math.min(ctx.canvas.height - 1, Math.round(y)));
        var d = ctx.getImageData(cx, cy, 1, 1).data;
        if (d[3] === 0) { return null; }
        var h = function (nn) { var s = (nn | 0).toString(16); return s.length === 1 ? '0' + s : s; };
        return '#' + h(d[0]) + h(d[1]) + h(d[2]);
      } catch (e) {
        return null;
      }
    },

    loadImageEl: function (src) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = function () { reject(new Error('image load failed')); };
        img.src = src;
      });
    },

    notify: function (message) {
      if ($.notify) {
        $.notify({ message: message }, {
          type: 'info',
          placement: { from: 'top', align: 'center' },
          delay: 3000
        });
      }
    },

    /* --------------------------------------------------------------- */
    /* Export (vector-preserving)                                       */
    /* --------------------------------------------------------------- */

    save: function () {
      if (!window.PDFLib) { return; }
      this.saveCurrentPageOverlay();
      this.$el.addClass('pe-saving');
      var that = this;

      this.buildPdf().then(function (bytes) {
        var name = that.options.fileName || 'proofsheet.pdf';
        var file;
        try {
          file = new File([bytes], name, { type: 'application/pdf' });
        } catch (err) {
          file = new Blob([bytes], { type: 'application/pdf' });
          file.name = name;
        }
        if (typeof that.options.onSave === 'function') { that.options.onSave(file); }
        if (that.windowView) { that.windowView.close(); }
      })['catch'](function (err) {
        that.$el.removeClass('pe-saving');
        if (window.console) { console.error('PDF export failed', err); }
        if ($.notify) {
          $.notify({ message: _.i18n('pdfEditor.exportError', null, 'Could not export the PDF.') },
            { type: 'danger', placement: { from: 'top', align: 'center' } });
        }
      });
    },

    buildPdf: function () {
      var PDFLib = window.PDFLib;
      var that = this;
      meshDrawSubdiv = MESH_SUBDIV; // always export at full mesh quality
      var pages = _.filter(this.pages, function (p) { return !p.deleted; });

      // In create mode there's no source document to copy vector pages from.
      var loadSrc = this.originalBytes ?
        PDFLib.PDFDocument.load(this.originalBytes) : Promise.resolve(null);

      return loadSrc.then(function (srcDoc) {
        return PDFLib.PDFDocument.create().then(function (outDoc) {
          var chain = Promise.resolve();
          _.each(pages, function (pg) {
            chain = chain.then(function () { return that.addExportPage(outDoc, srcDoc, pg); });
          });
          return chain
            .then(function () { return that.attachEditorState(outDoc); })
            .then(function () { return outDoc.save(); });
        });
      });
    },

    addExportPage: function (outDoc, srcDoc, pg) {
      var that = this;
      var hasOverlay = pg.overlayJSON && pg.overlayJSON.objects && pg.overlayJSON.objects.length;
      // Blank (created) pages have no source vector content, so they are always
      // composited as a raster page.
      var needsRaster = pg.blank || pg.rotation !== 0 || pg.rasterEdited || pg.srcHasRotate || pg.resized;

      if (needsRaster) {
        return this.composePagePng(pg).then(function (res) {
          return outDoc.embedPng(res.dataUrl).then(function (png) {
            var page = outDoc.addPage([res.wPt, res.hPt]);
            page.drawImage(png, { x: 0, y: 0, width: res.wPt, height: res.hPt });
          });
        });
      }

      return outDoc.copyPages(srcDoc, [pg.srcIndex]).then(function (copied) {
        var page = copied[0];
        outDoc.addPage(page);
        if (!hasOverlay) { return null; }
        return that.renderOverlayPng(pg).then(function (dataUrl) {
          return outDoc.embedPng(dataUrl).then(function (png) {
            var size = page.getSize();
            page.drawImage(png, { x: 0, y: 0, width: size.width, height: size.height });
          });
        });
      });
    },

    exportMultiplier: function (pg) {
      var maxDisp = Math.max(pg.baseW, pg.baseH);
      return Math.min(3, Math.max(1, 2000 / maxDisp));
    },

    // The page background for display/export: the natural bitmap, pre-rotated to
    // the page's current orientation. Returns a Promise<dataUrl>.
    displayBg: function (pg) {
      if (!pg.rotation) {
        var d = $.Deferred();
        d.resolve(pg.baseDataUrl);
        return this.asPromise(d);
      }
      return this.rotateDataUrl(pg.baseDataUrl, pg.rotation);
    },

    // Transparent overlay (objects only) rendered to a PNG data URL, sized to the
    // un-rotated page so it stamps 1:1 onto the copied vector page. Only used for
    // pages that keep their original vector content (rotation 0, not rasterized).
    renderOverlayPng: function (pg) {
      var that = this;
      var defer = $.Deferred();
      var staticCanvas = new fabric.StaticCanvas(null, {
        width: pg.baseW, height: pg.baseH
      });
      staticCanvas.loadFromJSON({ objects: pg.overlayJSON.objects }, function () {
        that.restoreWarpedImages(staticCanvas);
        var url = staticCanvas.toDataURL({ format: 'png', multiplier: that.exportMultiplier(pg) });
        staticCanvas.dispose();
        defer.resolve(url);
      });
      return this.asPromise(defer);
    },

    // Background (pre-rotated) + overlay flattened into one PNG in the page's final
    // orientation. Returns { dataUrl, wPt, hPt }. The display already shows this
    // orientation, so no extra rotation is applied here.
    composePagePng: function (pg) {
      var that = this;
      var defer = $.Deferred();
      var swap = (pg.rotation === 90 || pg.rotation === 270);
      var dispW = swap ? pg.baseH : pg.baseW;
      var dispH = swap ? pg.baseW : pg.baseH;
      var staticCanvas = new fabric.StaticCanvas(null, { width: dispW, height: dispH });
      var objects = (pg.overlayJSON && pg.overlayJSON.objects) ? pg.overlayJSON.objects : [];

      staticCanvas.loadFromJSON({ objects: objects }, function () {
        that.restoreWarpedImages(staticCanvas);
        that.displayBg(pg).then(function (bgUrl) {
          fabric.Image.fromURL(bgUrl, function (img) {
            img.set({ scaleX: dispW / img.width, scaleY: dispH / img.height });
            staticCanvas.setBackgroundImage(img, function () {
              var flat = staticCanvas.toDataURL({ format: 'png', multiplier: that.exportMultiplier(pg) });
              staticCanvas.dispose();
              defer.resolve({
                dataUrl: flat,
                wPt: swap ? pg.ptH : pg.ptW,
                hPt: swap ? pg.ptW : pg.ptH
              });
            });
          }, { crossOrigin: 'anonymous' });
        });
      });
      return this.asPromise(defer);
    },

    rotateDataUrl: function (dataUrl, deg) {
      var defer = $.Deferred();
      if (!deg) { defer.resolve(dataUrl); return this.asPromise(defer); }
      var img = new Image();
      img.onload = function () {
        var swap = (deg === 90 || deg === 270);
        var canvas = document.createElement('canvas');
        canvas.width = swap ? img.height : img.width;
        canvas.height = swap ? img.width : img.height;
        var ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(deg * Math.PI / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        defer.resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = function () { defer.resolve(dataUrl); };
      img.src = dataUrl;
      return this.asPromise(defer);
    },

    // pdf-lib uses native Promises; bridge jQuery deferreds to real Promises so
    // they chain cleanly with pdf-lib's await points.
    asPromise: function (defer) {
      return new Promise(function (resolve, reject) {
        defer.then(resolve, reject);
      });
    },

    /* --------------------------------------------------------------- */
    /* Right sidebar — images panel                                     */
    /* --------------------------------------------------------------- */

    // Print-area preview snapshots (options.referenceImages): reference only,
    // never inserted/exported, so the user can see where the logo sits on the
    // product while editing. Shown as a section inside the images sidebar.
    renderReferences: function () {
      var refs = this.options.referenceImages || [];
      if (!refs.length) {
        this.ui.refs.css('display', 'none');
        return;
      }
      var html = '';
      _.each(refs, function (ref, i) {
        var label = _.escape(ref.label || ('#' + (i + 1)));
        html += '<figure class="pe-ref" data-index="' + i + '" title="' +
          _.i18n('pdfEditor.referenceZoom', null, 'Click to enlarge') + '">' +
          '<div class="pe-ref-img"><img src="' + ref.url + '" alt="' + label + '"/></div>' +
          '<figcaption class="pe-ref-cap">' + label + '</figcaption>' +
          '</figure>';
      });
      this.ui.refsList.html(html);
      this.ui.refs.css('display', '');
    },

    // Populate the sidebar with the order's / product's / customer's images so
    // they're one click away from the canvas. Product goes in the first column,
    // Order + Customer stack in the second, matching how buyers think about the
    // artwork sources.
    renderImageSources: function () {
      var sources = this.options.imageSources || [];
      this._imgState = {};
      var that = this;
      var hasSources = false;

      _.each(sources, function (src) {
        if (!src || !src.key || typeof src.loader !== 'function') { return; }
        hasSources = true;
        that._imgState[src.key] = {
          source: src, first: 0, items: [], done: false, loading: false
        };
        var col = (src.key === 'product') ? that.ui.imgColA : that.ui.imgColB;
        col.append(
          '<section class="pe-src-group" data-key="' + src.key + '">' +
          '<div class="pe-src-head">' + _.escape(src.label || src.key) + '</div>' +
          '<div class="pe-src-grid"></div>' +
          '<div class="pe-src-status">' + _.i18n('common.loading', null, 'Loading…') + '</div>' +
          '</section>'
        );
        that.loadImageSourcePage(src.key);
      });

      var show = hasSources || (this.options.referenceImages || []).length;
      this.sidebarVisible = !!show;
      this.ui.sidebar.css('display', show ? '' : 'none');
      this.ui.refsToggleGroup.css('display', show ? '' : 'none');
      this.ui.refsToggle.toggleClass('active', !!show);
    },

    loadImageSourcePage: function (key) {
      var st = this._imgState && this._imgState[key];
      if (!st || st.loading || st.done) { return; }
      st.loading = true;
      var that = this;

      $.when(st.source.loader(st.first, SIDEBAR_PAGE_SIZE)).done(function (items) {
        items = items || [];
        var images = _.filter(items, ImageSourcePickerView.isInsertable);
        st.items = st.items.concat(images);
        st.first += items.length;
        if (items.length < SIDEBAR_PAGE_SIZE) { st.done = true; }
        st.loading = false;
        that.renderImageSourceGroup(key);
      }).fail(function () {
        st.loading = false;
        st.done = true;
        that.renderImageSourceGroup(key);
      });
    },

    renderImageSourceGroup: function (key) {
      var st = this._imgState && this._imgState[key];
      if (!st) { return; }
      var $group = this.$('.pe-src-group[data-key="' + key + '"]');
      if (!$group.length) { return; }
      var $grid = $group.find('.pe-src-grid');
      var $status = $group.find('.pe-src-status');

      if (!st.items.length) {
        $grid.empty();
        $status.css('display', '').text(st.done ?
          _.i18n('pdfEditor.noImagesHere', null, 'No images') :
          _.i18n('common.loading', null, 'Loading…'));
        return;
      }

      var html = '';
      _.each(st.items, function (a, i) {
        var name = a.get('fileName') || '';
        var thumb = ImageSourcePickerView.resolveThumbUrl(a);
        html += '<button type="button" class="pe-src-item" data-key="' + key + '" data-index="' + i +
          '" title="' + _.escape(name) + ' — ' +
          _.i18n('pdfEditor.clickToAdd', null, 'click to add to the page') + '">' +
          '<img src="' + thumb + '" alt="' + _.escape(name) + '"/></button>';
      });
      $grid.html(html);

      if (st.done) {
        $status.css('display', 'none').empty();
      } else {
        $status.css('display', '').html('<button type="button" class="pe-src-more" data-key="' + key + '">' +
          _.i18n('common.loadMore', null, 'Load more') + '</button>');
      }
    },

    onSidebarLoadMore: function (e) {
      this.loadImageSourcePage($(e.currentTarget).attr('data-key'));
    },

    onSidebarImageClick: function (e) {
      var el = $(e.currentTarget);
      var key = el.attr('data-key');
      var idx = parseInt(el.attr('data-index'), 10);
      var st = this._imgState && this._imgState[key];
      var attachment = (st && !isNaN(idx)) ? st.items[idx] : null;
      if (!attachment) { return; }
      // Product pictures are placed to fill the artboard; order / customer
      // logos keep their natural (half-page) drop size.
      var fit = (key === 'product') ? 'page' : 'half';
      this.insertImage(ImageSourcePickerView.resolveInsertUrl(attachment), { fit: fit });
    },

    onSidebarUploadClick: function () {
      this.ui.uploadInput.val('');
      this.ui.uploadInput.click();
    },

    onSidebarUploadChange: function (e) {
      var file = e.target.files && e.target.files[0];
      if (!file) { return; }
      // Blob URLs are same-origin, so the canvas stays untainted on export.
      var url = window.URL.createObjectURL(file);
      this._objectUrls.push(url);
      this.insertImage(url);
    },

    toggleSidebar: function () {
      this.sidebarVisible = !this.sidebarVisible;
      this.ui.sidebar.css('display', this.sidebarVisible ? '' : 'none');
      this.ui.refsToggle.toggleClass('active', this.sidebarVisible);
      if (this.fabricCanvas) { this.fitAndZoom(); }
    },

    onRefClick: function (e) {
      var idx = parseInt($(e.currentTarget).attr('data-index'), 10);
      var ref = (this.options.referenceImages || [])[idx];
      if (!ref) { return; }
      this.ui.refLightboxImg.attr('src', ref.url);
      this.ui.refLightboxCap.text(ref.label || '');
      this.ui.refLightbox.css('display', 'flex');
    },

    closeRefLightbox: function () {
      this.ui.refLightbox.css('display', 'none');
      this.ui.refLightboxImg.attr('src', '');
    },

    /* --------------------------------------------------------------- */
    /* Misc                                                             */
    /* --------------------------------------------------------------- */

    cancel: function () {
      if (this.windowView) { this.windowView.close(); }
    },

    showLoader: function (show) {
      if (this.ui.loader && this.ui.loader.length) {
        this.ui.loader.css('display', show ? 'block' : 'none');
      }
    },

    showError: function (msg) {
      this.showLoader(false);
      if (this.ui.error && this.ui.error.length) {
        this.ui.error.text(msg).css('display', 'flex');
      }
    },

    toHex: function (color) {
      if (!color) { return '#000000'; }
      if (/^#/.test(color)) { return color; }
      var m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!m) { return '#000000'; }
      var hex = function (n) { var s = parseInt(n, 10).toString(16); return s.length === 1 ? '0' + s : s; };
      return '#' + hex(m[1]) + hex(m[2]) + hex(m[3]);
    }
  });

  View.open = function (data) {
    require([
      'windows',
      'pdfEditorView'
    ], function (Windows, ViewCtor) {
      Windows.open({
        view: function () { return new ViewCtor(data); },
        id: 'pdf-editor:' + (data && data.id ? data.id : Date.now()),
        title: (data && data.title) || _.i18n('pdfEditor.title', null, 'PDF editor'),
        modal: true,
        bodyClass: 'pdf-editor-body',
        width: '92%',
        height: '92%'
      });
    });
  };

  Module.exports = View;
});

